// =============================================================================
// Project Management module (PROTOTYPE) — collections + method declarations
//
// Self-contained: all collections/methods are namespaced `pm`. See projectManagementLifecycles.ts
// for the lifecycle registry and ProjectManagementPipelinePage.tsx for the (small) wiring touch-points.
// =============================================================================

import { Duration } from 'luxon';
import { DriveGalleryPage } from '/imports/api/drive';
import { InventoryDelivery, InventoryPallet, InventorySite } from '/imports/api/inventory';
import { Mongo } from 'meteor/mongo';
import { Meteor } from 'meteor/meteor';
import { Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { DeclareMethod } from '/imports/methods';
import { formatCalendarDate } from '/imports/utils';
import { Roles, isLuminiaUser, accountHasAccounting } from '/imports/api/accounts';
import { strings } from '/imports/strings';
import { getProjectManagementLifecycle, ProjectManagementLifecycleFinance } from '/imports/api/projectManagementLifecycles';

/** Module access rule: super-users are admins (see all org projects); accounts holding the
 *  projectManagement role are members (see only projects they're a stakeholder on). */
export const projectManagementCanAccess = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.projectManagement);

/**
 * The ADMIN tier: sees and manages every project in the org. Super users, plus accounts holding BOTH the
 * org `admin` role and `projectManagement` — an org administrator running their own portfolio.
 * Holding `projectManagement` ALONE is the member/stakeholder tier: scoped to the projects they're linked
 * to as a stakeholder (e.g. an EPC contractor). Shared by the client UI and the server access helpers so
 * the two can never disagree about who is an admin.
 * NOTE: document approve/reject is deliberately NARROWER than this — super users only.
 * @param user - the logged-in user
 * @param account - their account in the current org
 */
export const projectManagementIsAdmin = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser
  || (!!account?.roles?.includes(Roles.admin) && !!account?.roles?.includes(Roles.projectManagement));

/** Who may bring a NEW project into the module — the New Project dialog and both tracker importers.
 *  Deliberately narrower than the admin tier: super users, or an account holding admin AND
 *  projectManagement AND luminia. An org admin without the luminia role manages the projects that exist
 *  but cannot create one. */
/**
 * A project name as it is stored: runs of whitespace collapsed to one space, ends trimmed. A browser shows
 * "PetSmart  Sunset" as "PetSmart Sunset", so a doubled space is invisible and then defeats a search
 * typed from what is on screen (QA finding, 2026-10-30).
 * @param name - the name as entered or imported
 * @returns the cleaned name
 */
export const projectManagementCleanName = (name?: string | null): string => (name || '').replace(/\s+/g, ' ').trim();

export const projectManagementCanCreateProject = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser
  || (!!account?.roles?.includes(Roles.admin) && !!account?.roles?.includes(Roles.projectManagement) && !!account?.roles?.includes(Roles.luminia));

/** Excusing a task requirement — the optional flag on a parameter or document: super users, or accounts
 *  holding the luminia role (Luminia's own people). Deliberately NOT the admin tier: an org admin sees the
 *  flag but may not change what a task is waiting on. */
export const projectManagementCanSetOptional = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  isLuminiaUser(user, account);

/** Financial visibility — the project Budget tab and the pipeline Finances view: super users, or an
 *  accounting role (accounting, or invoiceApprover which includes it) paired with projectManagement.
 *  Deliberately NOT the admin tier: Luminia's fee and the cash ledger are accounting's business, not
 *  every PM admin's. */
export const projectManagementCanSeeFinances = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser
  || (accountHasAccounting(account) && !!account?.roles?.includes(Roles.projectManagement));

const US_STATE_CODES = new Set(['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC']);
const US_STATE_NAMES: { [name: string]: string } = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD', 'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
};

/**
 * Best-effort US state code from a freeform address — projects have no structured state field, so the
 * pipeline's State filter and the stakeholder bulk view both read it back out of the text.
 *
 * Works from the END rather than matching one shape, because the real data holds several: a trailing
 * country and a comma'd zip ("…, CA, 92075, US"), no zip at all ("…, Plantation, FL"), and no comma
 * before the state ("…, Normal IL 61761"). Strip the country, strip the zip, and whatever two-letter
 * token is left at the end is the state. A spelled-out name anywhere is the fallback.
 * @param address - the project's freeform address
 * @returns the two-letter code, or undefined when nothing in the string is recognizable
 */
export const projectManagementStateFromAddress = (address?: string): string | undefined => {
  if (!address) return undefined;
  const tail = address.trim()
    .replace(/[,\s]+(?:USA?|United States)\s*$/i, '')
    .replace(/[,\s]+\d{5}(?:-\d{4})?\s*$/, '')
    .trim();
  const token = tail.match(/([A-Za-z]{2})\s*$/);
  if (token && US_STATE_CODES.has(token[1].toUpperCase())) return token[1].toUpperCase();
  const lower = ` ${address.toLowerCase()} `;
  for (const name of Object.keys(US_STATE_NAMES)) {
    if (lower.includes(` ${name} `) || lower.includes(`, ${name}`)) return US_STATE_NAMES[name];
  }
  return undefined;
};

/**
 * Who may see what Luminia makes: super users, or the invoiceApprover role. ONE rule for the per-project
 * figures (the Fees banner figure, the Funding Sources table, the Luminia Fees and Ancillary Fees tables,
 * and the funding source a change order draws on) and for the portfolio Total Fees roll-up — the person
 * authorising payments sees the margin, and the parts that compose it, on both.
 *
 * Deliberately narrower than the Budget tab it sits on: ACCOUNTING runs the project's money (cash in and
 * out, invoices) without seeing Luminia's margin on it. The data is withheld at the publications too, not
 * merely hidden in the client, so the figures never reach the wire.
 */
export const projectManagementCanSeeFees = (
  user?: { isSuperUser?: boolean } | null,
  account?: { roles?: string[] } | null,
): boolean => !!user?.isSuperUser || !!account?.roles?.includes(Roles.invoiceApprover);

/**
 * Who may see the project's FUNDING SOURCES: the fee viewers above, plus the luminia role. Wider than
 * the fee tables beside it on purpose — where the money comes from is Luminia's own business, while what
 * Luminia keeps of it stays with the people authorising payments. Gates the publication too, so the rows
 * reach only these callers.
 */
export const projectManagementCanSeeFundingSources = (
  user?: { isSuperUser?: boolean } | null,
  account?: { roles?: string[] } | null,
): boolean => projectManagementCanSeeFees(user, account) || !!account?.roles?.includes(Roles.luminia);

/** Who may approve or reject an invoice: super users, or the invoiceApprover role paired with
 *  projectManagement. Narrower than seeing the money — accounting reads the ledger without authorising a
 *  payment against it — and wider than it was, since this was the one thing only a super user could do. */
export const projectManagementCanApproveInvoices = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser
  || (!!account?.roles?.includes(Roles.invoiceApprover) && !!account?.roles?.includes(Roles.projectManagement));

/** Who may decide a document version — approve, reject, or put it back to pending: super users, or the
 *  admin tier (admin + projectManagement) holding the pmDocumentApprover grant as well. Deliberately
 *  narrower than the admin tier alone, which an org admin holds without being an approver. */
export const projectManagementCanApproveDocuments = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser
  || (projectManagementIsAdmin(user, account) && !!account?.roles?.includes(Roles.pmDocumentApprover));

/** Org-wide project VISIBILITY (see every org project, receive full project data): the admin tier plus
 *  finance viewers. Distinct from projectManagementIsAdmin, which alone unlocks mutations — accounting
 *  reads the whole portfolio's finances but edits nothing. */
export const projectManagementIsOrgWide = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  projectManagementIsAdmin(user, account) || projectManagementCanSeeFinances(user, account);

// --- A portfolio: an org-level grouping of projects (e.g. a tenant phase like "Dollar Tree Phase 1").
//     Deliberately thin — organizational metadata, NOT an access boundary. Future portfolio-scoped
//     features (master-schedule import, partner links, data-room presets) hang off this identity. ---
export interface ProjectManagementPortfolio extends Doc {
  orgId?: string;
  name: string;
  // Uploaded by hand, never looked up: a portfolio groups sites of one brand, so its mark is the one
  // the project header falls back to when a project has no logo of its own.
  logo?: FileMeta;
  note?: string;
}

// --- Project status: set by hand by the PM admin tier and the ONLY thing that colours a project (decided
//     2026-09-18 — the schedule-derived health it replaced marked moving projects red). Missing = moving. ---
// 'deferred' is parked rather than stopped: work that cannot progress for a known period but is still
// expected to finish. Like the other two exception statuses it carries a reason.
export const projectManagementProjectStatuses = ['moving', 'complete', 'onHold', 'deferred', 'canceled'] as const;
/** The statuses that have to say why. */
export const projectManagementStatusNeedsReason = (status?: ProjectManagementProjectStatus): boolean =>
  status === 'onHold' || status === 'deferred' || status === 'canceled';
export type ProjectManagementProjectStatus = typeof projectManagementProjectStatuses[number];
export const projectManagementProjectStatus = (p: { status?: ProjectManagementProjectStatus }): ProjectManagementProjectStatus => p.status ?? 'moving';

// Where each status sits in a list: work in progress, then the two pauses, then the two endings. Deferred
// follows on hold because both are pauses that may resume; complete precedes canceled because a finished
// project is still an asset someone looks up, while a canceled one is a dead record. ONE definition, read
// by the pipeline's default row order and by the Table view's Status column, so the two cannot disagree —
// the same five values ordered two ways in one table reads as a bug.
export const projectManagementStatusOrder: { [k in ProjectManagementProjectStatus]: number } = {
  'moving': 0, 'onHold': 1, 'deferred': 2, 'complete': 3, 'canceled': 4,
};

/** A status's display name. Here rather than beside the badge that renders it, because exports name a
 *  status too and server code cannot import a React module to ask. */
export const projectStatusLabel = (status: ProjectManagementProjectStatus): string =>
  status === 'complete' ? strings.Complete()
    : status === 'onHold' ? strings.OnHold()
      : status === 'deferred' ? strings.Deferred()
        : status === 'canceled' ? strings.Canceled()
          : strings.InProgress();   // also the fallback for a project with no stored status

// A reviewed milestone's state. 'readyForReview' is set by the engine when the milestone's work is in;
// the other two are the reviewer's decision. A deficiency always carries a note, and every transition is
// written to History — that trail IS the deficiency log, which is a running conversation, not one field.
export const projectManagementMilestoneReviewStatuses = ['readyForReview', 'approved', 'deficiency'] as const;
export type ProjectManagementMilestoneReviewStatus = typeof projectManagementMilestoneReviewStatuses[number];

/** Display label for a milestone's review status. */
export const projectManagementMilestoneReviewLabel = (status?: ProjectManagementMilestoneReviewStatus): string => {
  switch (status) {
    case 'readyForReview': return strings.ReviewReadyForReview();
    case 'approved': return strings.ReviewApproved();
    case 'deficiency': return strings.ReviewDeficiency();
    default: return '';
  }
};

/** Badge colour for a milestone's review status — the same three the document badges already use. */
export const projectManagementMilestoneReviewBadge = (status?: ProjectManagementMilestoneReviewStatus): string => {
  switch (status) {
    case 'approved': return 'approved';
    case 'deficiency': return 'rejected';
    default: return 'pending';
  }
};

// --- A managed project (the spine; mirrors a subset of the real Project entity) ---
export interface ProjectManagementProject extends Doc {
  orgId?: string;
  name: string;
  address?: string;
  onsiteContactName?: string;
  onsiteEmail?: string;
  onsitePhone?: string;
  clientId?: string;
  utilityId?: string;        // ProjectManagementUtility — the serving utility
  sgipId?: string;
  portfolioId?: string;      // org-level grouping (ProjectManagementPortfolio)
  lifecycleKey: string;
  lifecycleVersion: number;  // pinned at creation
  scheduleTierKey?: string;  // which schedule tier's milestone template was instantiated
  status?: ProjectManagementProjectStatus;  // hand-set; read through projectManagementProjectStatus() (missing = moving)
  statusNote?: string;       // the reason — required for onHold / canceled
  statusChangedAt?: Date;
  statusChangedBy?: string;  // userId
  capacityKwDc?: number;
  storageKwh?: number;
  soldAtNtp?: boolean;       // terminal early-exit branch (supported by all verticals)
  // Luminia's own handle for the project — PREFIX + a per-vertical sequence (RIC0001). Allocated by the
  // server at creation, unique across every vertical and org, never edited and never reused. Distinct from
  // externalId, which is the VERTICAL's number (RIC's tracker #) and is editable.
  luminiaId?: string;
  externalId?: string;       // external key for import/dedupe (e.g. RIC #)
  logo?: FileMeta;           // the SITE's own brand mark (the tenant/occupier), shown beside the title
  ownerId?: string;          // ProjectManagementOwner — the off-taker / site host
  epcId?: string;            // ProjectManagementEpc — the EPC contractor
  lat?: number;              // geocoded site latitude (for the map view)
  lng?: number;              // geocoded site longitude
}

// --- A schedule milestone, instantiated per-project from the lifecycle's schedule-tier template.
//     Planned dates come from the template offsets; actuals are filled as the project progresses. ---
export interface ProjectManagementMilestone extends Doc {
  projectManagementProjectId: string;
  key: string;               // template key (stable within a lifecycle)
  label: string;
  phase: string;
  canonicalStage: string;    // one of the cross-vertical canonical stages
  ownerRole?: string;
  ownerStakeholderIds?: string[];  // assigned memberships; absent/empty = unassigned
  order: number;
  plannedDate?: Date;
  actualDate?: Date;
  health?: 'red' | 'yellow' | 'green';
  isPaymentTrigger?: boolean;
  requiredDocSlots?: string[]; // docType keys that gate this milestone's payment
  // --- Milestone review (verticals that declare it; RIC does not) ---
  // Stamped from the lifecycle template. A reviewed milestone does NOT complete itself when its tasks and
  // payments land: it goes to 'readyForReview' and waits for a reviewer, whose approval sets actualDate.
  reviewed?: boolean;
  reviewStatus?: ProjectManagementMilestoneReviewStatus;
  readyAt?: Date;            // when it first reached the reviewer (drives the approval-timeframe metric)
  reviewedAt?: Date;         // when the reviewer last decided
  reviewedByUserId?: string;
  reviewedByName?: string;
}

// --- Sources & Uses: a capital provider for the project (0–2; may be the same entity). Debt + tax equity
//     cover RIC/CEA/FTM; residential adds the pre-paid loan and the capital advance that bridges the
//     funding-draw lag (the completion kicker is money-in cadence — a cash event, not a source). ---
// 'prepaidPpa' is residential's inflow: the homeowner's single payment under a prepaid PPA. 'sgipIncentive'
// is the SGIP reservation the Fund fronts against on residential's SGIP projects — a source of cash like
// any other, and the only one that arrives as a rebate. Appended, so no existing funding source changes type.
export const projectManagementFundingSourceTypes = ['debt', 'taxEquity', 'grossItcRevenue', 'netItcRevenue', 'loan', 'capitalAdvance', 'prepaidPpa', 'sgipIncentive'] as const;
export type ProjectManagementFundingSourceType = typeof projectManagementFundingSourceTypes[number];

export interface ProjectManagementFundingSource extends Doc {
  projectManagementProjectId: string;
  type: ProjectManagementFundingSourceType;
  party?: string;            // funder name (data, not enum — e.g. "Realty Income", "NorthShore Energy")
  partyStakeholderId?: string; // when set, the funder is this project stakeholder; `party` is the fallback label
  estimatedAmount?: number;
  note?: string;
}

// --- The money ledger: inflows (funder draws) and outflows (EPC/installer payments), each tied to a
//     milestone and (for inflows) a funding source. Actuals reconcile against the estimated amounts.
//     `origin`/`qbRef` make this ready for the (later) QuickBooks pull — no schema change needed. ---
export interface ProjectManagementCashEvent extends Doc {
  projectManagementProjectId: string;
  direction: 'in' | 'out';
  fundingSourceId?: string;  // inflows: which source provided the cash
  payee?: string;            // outflows: who was paid (EPC, installer, …)
  payeeStakeholderId?: string; // when set, the payee is this project stakeholder; `payee` is the fallback label
  // A cash event's payment code (m0..m6 / lntp / fntp / wo3 / equipmentWo) — its stable identity across
  // the tracker import, the QuickBooks sync, and the funding-rollup constants. NOT a milestone _id and
  // no longer required to name a milestone (several codes group under one milestone; some, like
  // equipmentWo, name none).
  milestoneKey?: string;
  milestoneId?: string;      // the owning schedule milestone (one of the lifecycle's milestones); grouping/completion, like a task's
  label?: string;
  estimatedAmount?: number;
  actualAmount?: number;     // what was actually drawn/paid (may differ from estimated/invoiced)
  invoicedDate?: Date;
  paidDate?: Date;
  origin: 'manual' | 'quickbooks';
  // Who set the ESTIMATE last, where it matters for precedence: the executed EPC contract outranks the
  // financial model, so a model apply leaves an 'epcContract' row alone. Unset = tracker / hand / unknown.
  estimateSource?: 'model' | 'epcContract';
  qbRef?: string;            // QuickBooks id when pulled
  reference?: string;        // external payment reference (ACH transfer id, remittance / invoice number)
  changeOrder?: boolean;     // a change-order line (may need IC approval above the lifecycle threshold)
  // THIS project's share of the contract base a scheduled payment estimates (0–1), absent = the vertical's
  // default. RIC: the three work-order draws against Total Solar Cost, read through
  // projectManagementDrawShare(). Residential: the dealer payments against the System Purchase Price,
  // read through resTpoDealerShare(). Each vertical resolves its own; the field is the only thing shared.
  drawShare?: number;
  // RIC: THIS project's share of the dev fee a work-order draw releases (0–1), absent = the default
  // (projectManagementDevFeeReleaseSplit), read through projectManagementDevFeeShare().
  devFeeShare?: number;
}

// --- A named value from the financial model: estimated value vs actual (→ variance). Pre-filled by the
//     LLM parse (source='llm'), reviewed/edited by the PM (source='manual'). The `type` decides how
//     estimated/actual are stored & rendered (number is the default when absent). ---
export const projectManagementParameterTypes = ['string', 'integer', 'number', 'boolean', 'date', 'datetime'] as const;
export type ProjectManagementParameterType = typeof projectManagementParameterTypes[number];

// Grouping for the Parameters tab, derived from the RIC tracker's row-2 section headers (the RIC importer
// stamps it; manual/LLM parameters may be uncategorized → shown under "Other").
// Declaration order is the display order of the groups on the Parameters tab — keep it matching the
// tracker's left-to-right section order so the tab reads like the sheet.
/**
 * Whether a parameter carries an estimate AND an actual, as opposed to a single value.
 * @param parameter - the parameter (or a seed) to test
 * @returns true unless it has been explicitly opted out
 */
export const projectManagementTracksActual = (parameter?: { tracksActual?: boolean } | null): boolean =>
  parameter?.tracksActual !== false;

/**
 * Whether a parameter holds its value: the actual when it tracks one, else its single figure (which lives in
 * estimatedValue). This is the test a task's Completion count and its requirement checklist run — a
 * parameter that never tracks an actual is complete once its value is set.
 */
export const projectManagementParameterFilled = (p: { tracksActual?: boolean; estimatedValue?: unknown; actualValue?: unknown }): boolean => {
  const v = projectManagementTracksActual(p) ? p.actualValue : p.estimatedValue;
  return v != null && v !== '';
};

export const projectManagementParameterCategories = ['siteDetails', 'projectDetails', 'links', 'agreements', 'siteReview', 'permitting', 'equipment', 'workOrders', 'epcPayments', 'construction', 'modeled', 'review'] as const;
export type ProjectManagementParameterCategory = typeof projectManagementParameterCategories[number];
/**
 * The parameter categories a member (an external stakeholder, e.g. an EPC) never receives. These three are
 * the money; everything else is operational, and withholding it is what left a contractor unable to tell
 * what their own task was waiting on. Named rather than inferred so a new financial category has to be
 * added here deliberately.
 */
export const projectManagementMemberWithheldParamCategories: ProjectManagementParameterCategory[] = ['workOrders', 'epcPayments', 'modeled'];

export interface ProjectManagementParameter extends Doc {
  projectManagementProjectId: string;
  taskId?: string;           // owning process task (tracker columns roll up under tasks — 2026-08-05 mapping)
  name: string;
  type?: ProjectManagementParameterType;
  category?: ProjectManagementParameterCategory;
  // estimatedValue is THE value when tracksActual is false — which is the common case. It keeps that
  // name because every writer already fills it and renaming would migrate thousands of rows for nothing.
  estimatedValue?: number | string | boolean | Date;
  actualValue?: number | string | boolean | Date;
  // Stamped from the lifecycle seed. ABSENT MEANS ON — tracking an actual is the default, and this is
  // set to false only to opt a parameter out. Off means a single figure: one Value column, no variance,
  // and nothing to promote. Read it through projectManagementTracksActual, never directly.
  tracksActual?: boolean;
  unit?: string;
  // Nice to have, not required: an optional parameter is shown and filled like any other but is excluded
  // from its task's completion test, so a blank one does not hold the task open.
  optional?: boolean;
  notes?: string;            // free-text notes / status (the edit modal suggests scheduled/submitted/approved/completed/executed)
  source?: 'manual' | 'llm' | 'model';   // 'model' = deterministic BTA-model parse; 'llm' = legacy AI parse
}

/** Display heading for a parameter category (uncategorized → "Other"). */
export const projectManagementParameterCategoryLabel = (c?: ProjectManagementParameterCategory): string => {
  switch (c) {
    case 'siteDetails': return strings.CategorySiteDetails();
    case 'projectDetails': return strings.CategoryProjectDetails();
    case 'links': return strings.CategoryLinks();
    case 'agreements': return strings.CategoryAgreements();
    case 'siteReview': return strings.CategorySiteReview();
    case 'permitting': return strings.CategoryPermitting();
    case 'equipment': return strings.CategoryEquipment();
    case 'workOrders': return strings.CategoryWorkOrders();
    case 'epcPayments': return strings.CategoryEpcPayments();
    case 'construction': return strings.CategoryConstruction();
    case 'modeled': return strings.CategoryModeled();
    case 'review': return strings.CategoryReview();
    default: return strings.Other();
  }
};

// --- Unified append-only project timeline: one row per meaningful change to a project or its children;
//     never updated or removed (active stays true). Replaces the old per-entity StageTransition +
//     ParameterHistory logs. Detailed views (e.g. a parameter's history) are projections — filter the feed
//     by `kind` + `ref.id`. The UI renders each row from the structured fields via strings.ts (no stored
//     human-readable summary, so the feed stays localizable). ---
export const projectManagementEventKinds = [
  'stageTransition', 'milestone', 'parameter', 'cashEvent', 'changeOrder', 'fundingSource', 'stakeholder',
  'document', 'task', 'purchaseOrder', 'link', 'project', 'inventory', 'invoice',
] as const;
export type ProjectManagementEventKind = typeof projectManagementEventKinds[number];

// A single field-level diff, reused by every producer that reports a from→to transition.
export interface ProjectManagementEventChange {
  field: string;
  from?: string | number | boolean | Date;
  to?: string | number | boolean | Date;
}

export interface ProjectManagementProjectEvent extends Doc {
  projectManagementProjectId: string;
  kind: ProjectManagementEventKind;
  action: string;              // kind-scoped verb: 'created' | 'updated' | 'deleted' | 'moved' | 'completed' | …
  ref?: { collection: string; id: string };   // subject entity — for deep-link + filtering a detail view
  label?: string;              // subject's display name AT EVENT TIME (denormalized snapshot; survives renames/deletes)
  changes?: ProjectManagementEventChange[];    // from→to diffs (parameter edits, stage move, CO status …)
  meta?: { [key: string]: string | number | boolean | Date | null };  // kind-specific scalars (direction, amount, note …)
  // Copied from the document when the subject is a Budget attachment, so the publication can withhold the
  // event from whoever may not see that attachment (its label is the file name, which can carry an amount).
  budgetRef?: { kind: ProjectManagementBudgetAttachmentKind, id: string };
}

// --- Notifications: the "directed" project events that alert a specific person. Each type maps to a
//     producer (owner assignment, doc-approval request, doc decision, change-order decision, being added
//     as a stakeholder, invoice upload, invoice approved). Delivered in-app and/or by email per the recipient's preferences below. ---
/** What one run of the daily document digest did: the Pacific day it covered and what it found there. */
export type ProjectManagementDocumentDigestResult = { day: string, projects: number, uploads: number, approvals: number, awaiting: number, inboxEmails: number, approverEmails: number };
export type ProjectManagementVerticalDigestResult = { day: string, projects: number, uploads: number, approvals: number, awaiting: number, sent: boolean, to?: string };
// The digest's calendar: a wall-clock day for people in California, whatever the server's zone.
export const projectManagementDigestZone = 'America/Los_Angeles';

export const projectManagementNotificationTypes = [
  'assignment', 'documentApproval', 'documentDecision', 'changeOrderDecision', 'stakeholderAdded', 'invoiceUploaded', 'invoiceApproved', 'message',
] as const;
export type ProjectManagementNotificationType = typeof projectManagementNotificationTypes[number];
// The types a PERSON can receive, for the per-account preference table. The invoice notifications go to
// the inbox addresses in Settings, never to an account, so a personal toggle for them would do nothing.
export const projectManagementPersonalNotificationTypes = projectManagementNotificationTypes.filter((t) => t !== 'invoiceUploaded' && t !== 'invoiceApproved');
export const projectManagementNotificationChannels = ['inApp', 'email'] as const;
export type ProjectManagementNotificationChannel = typeof projectManagementNotificationChannels[number];

// Per-account preferences (opt-OUT model — absent means both channels on). `off[type]` lists the channels
// the account has muted for that notification type. Scoped to an account since PM is org-scoped.
export interface ProjectManagementNotificationPreference extends Doc {
  accountId: string;
  off?: { [type: string]: ProjectManagementNotificationChannel[] };
}

/** True when a channel is enabled for a type given the account's preference (default on when unset). */
export const projectManagementNotifyEnabled = (
  pref: ProjectManagementNotificationPreference | undefined,
  type: ProjectManagementNotificationType,
  channel: ProjectManagementNotificationChannel,
): boolean => !pref?.off?.[type]?.includes(channel);

// --- StakeholderProfile: the ORG-LEVEL identity of a person or company that participates in projects
//     (the off-taker's PM, an EPC contractor, a client rep…). One row per real entity in the org — the
//     single source of truth for their name/kind/email/login. Their involvement in a specific project is
//     a ProjectManagementStakeholder (the membership) that points here. Distinct from ProjectManagementParty
//     (the Owner/Epc/Utility company registries behind project.ownerId/epcId/utilityId). ---
export interface ProjectManagementStakeholderProfile extends Doc {
  orgId?: string;
  name: string;
  kind: 'internal' | 'external';
  organization?: string;
  email?: string;
  accountId?: string;          // optional link to an org Account (identity + app-roles for scoped login)
  // The party (EPC registry entry) this person works for. What lets a lifecycle's 'partner' tasks find
  // their owner: a project's epcId names the party, the party names its people.
  partyId?: string;
  // Tasks this person picks up on every NEW project of a vertical. Applied once, at creation, to a task
  // nobody owns yet; afterwards the assignment is an ordinary one and this list has no further say. Held
  // on the profile so deleting the person takes their defaults with them.
  defaultTasks?: { lifecycleKey: string; templateKey: string }[];
}

// --- Stakeholder (membership / "Party ↔ Project"): one participant's involvement in ONE project. Identity
//     (name/kind/email/login) lives on the StakeholderProfile it points at; the membership is a pure link and
//     carries no roles — ANY stakeholder is assignable to ANY task (assignment is manual/explicit). Every
//     stakeholder FK in the module (task/milestone owner, funding party, cash payee, PO supplier) references a
//     membership _id. ---
export interface ProjectManagementStakeholder extends Doc {
  projectManagementProjectId: string;
  profileId: string;           // → ProjectManagementStakeholderProfile (the org-level identity)
  notes?: string;              // free-form notes about this stakeholder's involvement in THIS project
}

// --- A project contact: a person to reach (site host rep, client PM, …), lighter than a Stakeholder
//     (no role/ownership/access semantics — purely a directory entry). ---
export interface ProjectManagementContact extends Doc {
  projectManagementProjectId: string;
  name: string;
  email?: string;
  phone?: string;
  title?: string;
}

// --- A note on a task: one entry in that task's feed, authored and timestamped. Distinct from the task's
//     own `notes` string, which was a single inline scratchpad the feed replaces — that field is left in
//     place for the handful of rows that still carry one. ---
export interface ProjectManagementTaskNote extends Doc {
  projectManagementProjectId: string;   // denormalised off the task, so the publication can scope by project
  projectManagementTaskId: string;
  body: string;
}

// --- A project link: a labeled external URL (SharePoint / Drive folder, design tool, dashboard, …).
//     Lightweight, like a contact — no versioning or access semantics, just a bookmark on the project. ---
export interface ProjectManagementLink extends Doc {
  projectManagementProjectId: string;
  label: string;
  url: string;
}

// --- Free-form project invoices: an EPC (or a super user) uploads any invoice file to a project. Not
//     seeded/pre-populated and not tied to a cash event — the uploader manages them (CRUD); super users
//     see them all in the Budget tab. ---
export interface ProjectManagementProjectInvoice extends Doc {
  projectManagementProjectId: string;
  label: string;
  file: FileMeta;
  vendor?: string;          // the issuing company, pre-filled by the AI read of the file; the uploader may override it.
  invoiceNumber?: string;   // the vendor/EPC's OWN invoice number, as they entered it — their reference, shown back to them. NOT the QB key.
  // Luminia's canonical invoice number = the QuickBooks Bill "Bill no." (DocNumber) — the deterministic match
  // key, unique per project. Defaults to the EPC's invoiceNumber on upload; a super user can override it (e.g.
  // to a clean Luminia AP number) without disturbing what the EPC sees. Super-user surface only.
  luminiaInvoiceNumber?: string;
  amount?: number;          // invoice total (EPC-entered); cross-checked against the QB bill amount.
  memo?: string;            // free-form note the uploader adds alongside the invoice.
  // Which money-out cash 'uses' this invoice pays, split by amount (set by a super user). One invoice can
  // cover several milestone payments (e.g. M2+M3+M4). Empty until a super user allocates it.
  allocations?: { cashEventId: string; amount: number }[];
  // Reconciliation status, stamped when a matching QuickBooks bill is pulled (Tier-0 invoice match).
  qbBillId?: string;
  billedAt?: Date;
  paidAt?: Date;
  // Super-user approval (Budget tab). approvedBy = the userId that approved; approvedByName denormalized for display.
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  active?: boolean;
  createdByUserId?: string;
  createdByName?: string;
}

// --- Per-stage tasks, instantiated from the lifecycle's task templates ---
export interface ProjectManagementTask extends Doc {
  projectManagementProjectId: string;
  milestoneId: string;          // owning ProjectManagementMilestone (every task belongs to one)
  stage: string;
  templateKey: string;
  label: string;
  // What the task IS — a standing explanation shown on the card and as the list's tooltip. Distinct from
  // `notes` below, which is this project's running commentary on doing it.
  description?: string;
  role?: string;
  // The memberships this task is assigned to. Several, because work is shared — and this array IS the
  // member access rule, not a display field: a member sees a task only when their membership is in it.
  ownerStakeholderIds?: string[];
  // Copied from the template at creation, so a later template edit cannot re-route an existing task.
  defaultOwner?: 'partner' | 'luminia';
  requiredDocSlots?: string[];  // docType keys this task delivers (upload from the task; all-approved can complete it)
  status: 'open' | 'done';
  // When the task's work finished. On the auto-computed path this is DERIVED — the latest of its
  // requirements' own dates (a document's approval, a parameter's entry), not the clock at evaluation
  // time. A super user's manual tick and the RIC import still stamp the moment they ran.
  completedAt?: Date;
  notes?: string;               // free-form working notes, edited inline on the task card
  // Reported schedule dates, entered as calendar days on the task card. Each must fall ON OR BEFORE the
  // owning milestone's corresponding date — estimatedDate against plannedDate, actualDate against
  // actualDate — which is what projectManagementTaskDateAfterMilestone enforces. Distinct from
  // completedAt: these are what a person REPORTS, that is when the requirements landed.
  estimatedDate?: Date;
  actualDate?: Date;
}

// --- Logical document (a typed slot). The "current" file is the latest approved/pending version. ---
export interface ProjectManagementDocument extends Doc {
  projectManagementProjectId: string;
  docType: string;
  label: string;
  stage?: string;          // expected-by stage (from lifecycle doc slot)
  requirement?: 'required' | 'conditional';
  condition?: string;
  fromTemplate?: boolean;  // generated from the lifecycle's docSlots
  budgetRef?: { kind: ProjectManagementBudgetAttachmentKind, id: string };  // budget-record attachment, managed from the Budget tab; seen only by who sees it there (projectManagementHiddenBudgetKinds)
  currentVersionId?: string;
  status: 'none' | 'pending' | 'approved' | 'rejected';
  // As on a parameter: an optional document is still listed and still uploadable, but an unapproved one
  // does not hold its task open. Distinct from `requirement` above, which records WHY a slot might not
  // apply and gates nothing.
  optional?: boolean;
  // Where the LLM read of this upload has got to. Reading happens off the upload path and can take a
  // minute (text extraction, then the model), so this is what the document row has to show for itself
  // in the meantime — without it an upload looks finished while the work is still running.
  aiRead?: ProjectManagementAiRead;
}

export interface ProjectManagementAiRead {
  status: 'reading' | 'done' | 'failed';
  startedAt: Date;
  finishedAt?: Date;
  suggested?: number;
  error?: string;
}

// A read that never reported back — the server restarted mid-job, say — must not leave a row spinning
// for ever. Past this, the UI stops believing a 'reading' stamp.
const AI_READ_STALE_MILLIS = 10 * 60 * 1000;

/**
 * Whether this document is being read by the LLM right now, as far as anyone can tell.
 * @param doc - the document
 * @returns true while a read is in flight and recent enough to still be plausible
 */
export const projectManagementIsReadingWithAi = (doc?: { aiRead?: ProjectManagementAiRead } | null): boolean => {
  const read = doc?.aiRead;
  if (read?.status !== 'reading') return false;
  return Date.now() - new Date(read.startedAt).valueOf() < AI_READ_STALE_MILLIS;
};

// --- A structured change order: originates from the EPC/partner (in-platform, never email), carries the
//     written change + rationale, its dollar value, attachments for the back-and-forth, and a
//     submission → review → acceptance workflow. Acceptance posts a money-out ledger line (changeOrder:
//     true) so the CO lands in the project's cash picture; the external-portal intake (later slice)
//     creates these same records. ---
export interface ProjectManagementChangeOrder extends Doc {
  projectManagementProjectId: string;
  title: string;
  rationale?: string;
  value?: number;
  fundingSourceId?: string;    // which funding source pays this change (FTM requirement)
  status: 'submitted' | 'underReview' | 'accepted' | 'rejected';
  decisionNote?: string;
  decidedAt?: Date;
  cashEventId?: string;        // the money-out cash event posted on acceptance (removed if un-accepted)
  // The invoice this change order was RAISED FROM (Budget → Allocate). Provenance, not payment: an invoice
  // pays a change order by allocating to the cash event acceptance posts, which is the cashEventId above.
  invoiceId?: string;
}

// --- A project-level purchase order: who ordered what and when, regardless of source. The order date is
//     the accountability anchor ("you approved and ordered this two months ago — it's in the tracker");
//     serials are recorded for the fund. Distinct from bulk inventory (which lives above the project
//     level); `source` says whether the item came from Luminia inventory or an external party. ---
export interface ProjectManagementPurchaseOrder extends Doc {
  projectManagementProjectId: string;
  reference: string;             // PO number / reference
  orderDate?: Date;
  equipmentType?: string;
  serials?: string;              // serial number list (free text)
  source: 'inventory' | 'external';
  supplierStakeholderId?: string; // procuring party (e.g. the EPC) when a project stakeholder
  supplier?: string;              // fallback label when not a stakeholder
  value?: number;
  note?: string;
}

// --- Immutable version of a document (the version-control chain) ---
export interface ProjectManagementDocumentVersion extends Doc {
  projectManagementDocumentId: string;
  projectManagementProjectId: string;
  versionNumber: number;
  // Where the bytes are. Since 2026-10-23 they live in the Documents module, in the project's own folder,
  // and `file` points at them there — so every reader that has always done `version.file.url` carries on
  // working, and a row written before that day is indistinguishable from one written after it. The two
  // ids below say which drive file and version these bytes are, for the rows that have them.
  file: FileMeta;
  driveFileId?: string;
  driveVersionId?: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded';
  changeNote?: string;
  approvedAt?: Date;
  rejectedReason?: string;
  // Written only by the bulk importer (server/projectManagementImport.ts). It is on the row rather than
  // in a side table because two things read it: a re-run, to skip what it already filed, and the text
  // extraction job, to know this version must never be handed to a model.
  importRef?: { kind: string, path: string, docType: string };
  // Copied from the document for a Budget attachment's versions, for the same reason as on the event.
  budgetRef?: { kind: ProjectManagementBudgetAttachmentKind, id: string };
}

export const ProjectManagementProjects = new Mongo.Collection<ProjectManagementProject>('projectManagementProjects');
export const ProjectManagementProjectEvents = new Mongo.Collection<ProjectManagementProjectEvent>('projectManagementProjectEvents');
export const ProjectManagementTasks = new Mongo.Collection<ProjectManagementTask>('projectManagementTasks');
export const ProjectManagementDocuments = new Mongo.Collection<ProjectManagementDocument>('projectManagementDocuments');
export const ProjectManagementDocumentVersions = new Mongo.Collection<ProjectManagementDocumentVersion>('projectManagementDocumentVersions');

// Extracted plain text per document version, for content search. Server-only: NEVER published (can be
// hundreds of KB per file); the client searches via projectManagementSearchDocuments.
export interface ProjectManagementDocumentText {
  _id?: string;
  projectManagementProjectId: string;
  projectManagementDocumentId: string;
  versionId: string;
  text: string;
  extractedAt: Date;
  // Stamped by the LLM parse job so the boot backfill (which re-extracts missing texts) can't re-parse
  // an already-parsed version. A NEW version gets its own row, so a re-upload does parse again.
  llmParse?: { at: Date, provider?: string, model?: string, suggested?: number, error?: string };
}
export const ProjectManagementDocumentTexts = new Mongo.Collection<ProjectManagementDocumentText>('projectManagementDocumentTexts');

// --- An LLM's PROPOSED estimated value for a task-linked parameter, read out of an uploaded document.
//     Review-before-apply: nothing is written to the parameter until a human accepts. Suggestions only
//     ever target a parameter whose estimated value is blank, and acceptance re-checks that. ---
export interface ProjectManagementParameterSuggestion extends Doc {
  projectManagementProjectId: string;
  projectManagementDocumentId: string;
  versionId: string;
  parameterId: string;
  taskId?: string;
  name: string;                      // parameter name, denormalised for display
  value: ProjectManagementParameterValue;
  status: 'proposed' | 'accepted' | 'rejected';
  provider?: string;
  model?: string;
  decidedAt?: Date;
  decidedByUserId?: string;
  decidedByName?: string;
}
export const ProjectManagementParameterSuggestions = new Mongo.Collection<ProjectManagementParameterSuggestion>('projectManagementParameterSuggestions');

// --- QuickBooks Bill → Use suggestions. Tier 1 (a milestone token on the bill) writes the placeholder
//     Use directly and never lands here; Tier 2 (the bill's vendor is the project's EPC and its amount
//     matches an unfilled Use estimate) and Tier 3 (project known, milestone not) become a pending
//     suggestion a human confirms on the Budget tab, because a wrong actual on a financial milestone is
//     worse than a missing one. ---
export interface ProjectManagementCashSuggestion extends Doc {
  projectManagementProjectId: string;
  direction: 'out';
  qbBillId: string;              // Bill.Id
  qbLineNum?: number;            // the line within the bill this attributes (a bill can span projects)
  vendor?: string;               // Bill.VendorRef.name, for display
  docNumber?: string;            // Bill.DocNumber
  amount: number;                // the line amount attributed to this project (what it bills)
  paidAmount?: number;           // the line's share of what the bill has paid so far — what confirming writes as the actual
  invoicedDate?: Date;           // Bill.TxnDate
  paidDate?: Date;               // from the BillPayment, only once the bill is settled
  milestoneKey?: string;         // Tier 2's candidate; absent on Tier 3 (a human assigns it)
  tier: 2 | 3;
  status: 'pending' | 'accepted' | 'dismissed';
  decidedAt?: Date;
  decidedByUserId?: string;
}

export const ProjectManagementCashSuggestions = new Mongo.Collection<ProjectManagementCashSuggestion>('projectManagementCashSuggestions');
export const ProjectManagementMilestones = new Mongo.Collection<ProjectManagementMilestone>('projectManagementMilestones');
export const ProjectManagementFundingSources = new Mongo.Collection<ProjectManagementFundingSource>('projectManagementFundingSources');
export const ProjectManagementCashEvents = new Mongo.Collection<ProjectManagementCashEvent>('projectManagementCashEvents');
export const ProjectManagementParameters = new Mongo.Collection<ProjectManagementParameter>('projectManagementParameters');
export const ProjectManagementStakeholderProfiles = new Mongo.Collection<ProjectManagementStakeholderProfile>('projectManagementStakeholderProfiles');
export const ProjectManagementStakeholders = new Mongo.Collection<ProjectManagementStakeholder>('projectManagementStakeholders');
export const ProjectManagementChangeOrders = new Mongo.Collection<ProjectManagementChangeOrder>('projectManagementChangeOrders');
export const ProjectManagementPurchaseOrders = new Mongo.Collection<ProjectManagementPurchaseOrder>('projectManagementPurchaseOrders');
export const ProjectManagementContacts = new Mongo.Collection<ProjectManagementContact>('projectManagementContacts');
export const ProjectManagementTaskNotes = new Mongo.Collection<ProjectManagementTaskNote>('projectManagementTaskNotes');
export const ProjectManagementLinks = new Mongo.Collection<ProjectManagementLink>('projectManagementLinks');
export const ProjectManagementProjectInvoices = new Mongo.Collection<ProjectManagementProjectInvoice>('projectManagementProjectInvoices');
export const ProjectManagementNotificationPreferences = new Mongo.Collection<ProjectManagementNotificationPreference>('projectManagementNotificationPreferences');
export const ProjectManagementPortfolios = new Mongo.Collection<ProjectManagementPortfolio>('projectManagementPortfolios');

// --- Per-org PM settings: ONE row per org, holding that org's inboxes per vertical, edited by super users on
//     /project-management/settings for the org they have selected. The notification inboxes were four global
//     Settings fields until 2026-10-01; every org and vertical runs its own AP and document desks, so a project
//     reaches its inboxes through orgId + lifecycleKey, and a digest is one email per org and vertical. A blank
//     inbox sends nothing. ---
export interface ProjectManagementVerticalInboxes {
  invoiceNotifyEmail?: string;          // AP inbox: emailed on every member (EPC) invoice upload
  invoiceApprovedNotifyEmail?: string;  // emailed when an invoice is approved
  documentNotifyEmail?: string;         // the daily task-document digest (uploaded, approved, awaiting)
  documentApprovedNotifyEmail?: string; // emailed when a task document is approved
}
export const projectManagementVerticalSettingsFields = ['invoiceNotifyEmail', 'invoiceApprovedNotifyEmail', 'documentNotifyEmail', 'documentApprovedNotifyEmail'] as const;
export type ProjectManagementVerticalSettingsField = typeof projectManagementVerticalSettingsFields[number];
export interface ProjectManagementOrgSettings extends Doc {
  orgId: string;
  verticals?: { [lifecycleKey: string]: ProjectManagementVerticalInboxes };
}
export const ProjectManagementOrgSettings = new Mongo.Collection<ProjectManagementOrgSettings>('projectManagementOrgSettings');
/** One org's inboxes for one vertical, or nothing while neither has been set up (every inbox then reads blank). */
export const projectManagementVerticalInboxes = async (orgId?: string, lifecycleKey?: string): Promise<ProjectManagementVerticalInboxes | undefined> =>
  (orgId && lifecycleKey ? (await ProjectManagementOrgSettings.findOneAsync({ orgId }))?.verticals?.[lifecycleKey] : undefined);

// One document per idPrefix ({ _id: 'RIC', seq: 133 }) — the high-water mark behind luminiaId. Server-only in
// practice: never published, only ever touched by the allocator's atomic $inc.
export const ProjectManagementIdSequences = new Mongo.Collection<{ _id: string, seq: number }>('projectManagementIdSequences');

/** A project's Luminia id from its vertical's prefix and sequence number: RIC0001, widening past 9999. */
export const projectManagementLuminiaId = (prefix: string, seq: number): string => `${prefix}${String(seq).padStart(4, '0')}`;

// --- Party registries: the companies a project points AT rather than copies. Owners (off-takers / site
//     hosts) and EPCs are separate collections because they are separate pick-lists with separate
//     logo-lookup context, but they share one shape — see ProjectManagementParty. Holding them once
//     means a rename or a new logo lands on every project at once, and the logo is looked up ONE time
//     per company instead of once per project that names it. ---
export interface ProjectManagementParty extends Doc {
  orgId?: string;
  name: string;
  logo?: FileMeta;           // shown in the project header in place of the name when set
  website?: string;          // resolved domain the logo came from (also what a re-lookup starts from)
  note?: string;
}

export type ProjectManagementOwner = ProjectManagementParty;
export type ProjectManagementEpc = ProjectManagementParty;
export type ProjectManagementUtility = ProjectManagementParty;

export const ProjectManagementOwners = new Mongo.Collection<ProjectManagementOwner>('projectManagementOwners');
export const ProjectManagementEpcs = new Mongo.Collection<ProjectManagementEpc>('projectManagementEpcs');
export const ProjectManagementUtilities = new Mongo.Collection<ProjectManagementUtility>('projectManagementUtilities');

// --- Composite shapes returned by the read methods (method-based fetching; no pub/sub) ---
export interface ProjectManagementDocumentWithVersions extends ProjectManagementDocument {
  versions: ProjectManagementDocumentVersion[];
  currentVersion?: ProjectManagementDocumentVersion;
}

export interface ProjectManagementProjectDetail {
  project: ProjectManagementProject;
  events: ProjectManagementProjectEvent[];
  tasks: ProjectManagementTask[];
  documents: ProjectManagementDocumentWithVersions[];
  milestones: ProjectManagementMilestone[];
  fundingSources: ProjectManagementFundingSource[];
  cashEvents: ProjectManagementCashEvent[];
  parameters: ProjectManagementParameter[];
  stakeholders: ProjectManagementStakeholder[];
  stakeholderProfiles: ProjectManagementStakeholderProfile[];
  changeOrders: ProjectManagementChangeOrder[];
  purchaseOrders: ProjectManagementPurchaseOrder[];
  // Equipment held for this project. Inventory sites are their own records (the portfolio tracks far
  // more sites than there are projects), so these arrive via the site that carries the project's number.
  inventorySites: InventorySite[];
  inventoryPallets: InventoryPallet[];
  inventoryDeliveries: InventoryDelivery[];
  contacts: ProjectManagementContact[];
  links: ProjectManagementLink[];
  cashSuggestions: ProjectManagementCashSuggestion[];
  invoices: ProjectManagementProjectInvoice[];
  taskNotes: ProjectManagementTaskNote[];
}

// --- Sources & Uses roll-up (pure; usable by UI + reporting): money-in vs money-out, estimated vs actual. ---
export interface ProjectManagementFinancialSummary {
  inEstimated: number; inActual: number;
  outEstimated: number; outActual: number;
  netEstimated: number; netActual: number;  // net margin = in − out
  // The change-order share OF the outflow above, not an addition to it: accepting a change order posts a
  // money-out cash event, so it is already inside outEstimated/outActual. Split out so the spend can be
  // shown as contract vs change orders — the same base/CO split projectManagementChangeOrderNeedsIc uses.
  outChangeOrderEstimated: number; outChangeOrderActual: number;
}

/**
 * @param cashEvents - the project's cash ledger
 * @param opts - `inEstimated` replaces the ledger's money-in total for a vertical whose Sources are not
 *   drawn against (finance.sourcesFigure). It is the ESTIMATE only: the actual stays the ledger's, because
 *   an actual is money that arrived, and that is what reconciles against QuickBooks whatever the estimate
 *   was read from. netEstimated follows the estimate, so Sources − Uses still reads as the margin.
 */
export const projectManagementFinancialSummary = (
  cashEvents: ProjectManagementCashEvent[],
  opts?: { inEstimated?: number },
): ProjectManagementFinancialSummary => {
  const sum = (dir: 'in' | 'out', field: 'estimatedAmount' | 'actualAmount', changeOrdersOnly = false) =>
    cashEvents
      .filter((c) => c.direction === dir && (!changeOrdersOnly || c.changeOrder))
      .reduce((acc, c) => acc + (c[field] || 0), 0);
  const inEstimated = opts?.inEstimated ?? sum('in', 'estimatedAmount');
  const inActual = sum('in', 'actualAmount');
  const outEstimated = sum('out', 'estimatedAmount');
  const outActual = sum('out', 'actualAmount');
  return {
    inEstimated, inActual, outEstimated, outActual,
    netEstimated: inEstimated - outEstimated, netActual: inActual - outActual,
    outChangeOrderEstimated: sum('out', 'estimatedAmount', true), outChangeOrderActual: sum('out', 'actualAmount', true),
  };
};

/**
 * The Sources estimate for a vertical that reads it from its funding sources rather than its ledger.
 *
 * Every source's own stored estimate, added up — the same figure the Budget tab's Sources table totals,
 * which is the point: the banner and the table a reader opens next should not disagree. A source with no
 * amount contributes nothing rather than reading as zero dollars of funding.
 * @param fundingSources - the project's funding sources
 */
export const projectManagementFundingSourcesEstimate = (
  fundingSources: Pick<ProjectManagementFundingSource, 'estimatedAmount'>[],
): number => fundingSources.reduce((total, source) => total + (source.estimatedAmount || 0), 0);

/**
 * The ledger the Uses figure is totalled from, with a Use that is paid out in installments counted once.
 *
 * Residential's dealer payments (finance.dealerSchedule) are its build-cost Use (finance.buildCostUseCode)
 * paid out at the milestones, 90% at M3B and 10% at M4 — the same money twice over in the ledger: once as
 * the EPC Cost the fund model wrote, once as the two payments it is split into. Summed row by row, the
 * Uses tile read $201,773 against $142,883 of Sources on a project whose two sides tie. So while the
 * project carries the Use, the installments fold into it: they leave the ledger, and the Use's actual
 * becomes what they have paid unless accounting booked a figure on the Use itself — a QuickBooks bill can
 * be applied to either row, and the same money must not be read from both.
 *
 * Nothing declared, or no such Use on the project (a tracker-imported residential project the fund model
 * has not priced, whose payments ARE its only Uses): the ledger comes back as it is. RIC declares no
 * finance block, so its ledger is never touched here.
 * @param cashEvents - the project's cash ledger
 * @param finance - the vertical's finance declaration
 */
export const projectManagementUsesLedger = <T extends Pick<ProjectManagementCashEvent, 'direction' | 'milestoneKey' | 'actualAmount' | 'active'>>(
  cashEvents: T[],
  finance?: Pick<ProjectManagementLifecycleFinance, 'buildCostUseCode' | 'dealerSchedule'>,
): T[] => {
  const useCode = finance?.buildCostUseCode;
  const codes = (finance?.dealerSchedule || []).map((row) => row.code);
  if (!useCode || !codes.length) return cashEvents;
  const outflow = (c: T, code: string) => c.direction === 'out' && c.milestoneKey === code && c.active !== false;
  const use = cashEvents.find((c) => outflow(c, useCode));
  const installments = cashEvents.filter((c) => codes.some((code) => outflow(c, code)));
  if (!use || !installments.length) return cashEvents;
  const paid = installments.some((c) => typeof c.actualAmount === 'number')
    ? installments.reduce((sum, c) => sum + (c.actualAmount || 0), 0)
    : undefined;
  return cashEvents
    .filter((c) => !installments.includes(c))
    .map((c) => (c === use ? { ...c, actualAmount: c.actualAmount ?? paid } : c));
};

// How the model's estimated Total Solar Cost is drawn from the partner across the three work orders — the
// DEFAULT split, the one the RIC tracker's WO amounts followed. A project whose work orders were written
// differently (Home Depot: 65/25/10) overrides it per draw with cashEvent.drawShare, set together on the
// Budget tab so the three always total 100%. Applied to the Sources' ESTIMATES whenever Total Solar Cost
// changes; actuals still come from QuickBooks.
export const projectManagementSolarCostDrawSplit: { [milestoneKey: string]: number } = { lntp: 0.5, fntp: 0.4, wo3: 0.1 };
export const projectManagementTotalSolarCostParameter = 'Total Solar Cost';
/** The share of Total Solar Cost a draw estimates — the project's own over the default — or nothing for a row that is not a draw. */
export const projectManagementDrawShare = (c: Pick<ProjectManagementCashEvent, 'milestoneKey' | 'drawShare'>): number | undefined => {
  if (!c.milestoneKey || !(c.milestoneKey in projectManagementSolarCostDrawSplit)) return undefined;
  return typeof c.drawShare === 'number' ? c.drawShare : projectManagementSolarCostDrawSplit[c.milestoneKey];
};

// The financial model's Project Milestone Disbursement Table (Luminia Assumptions sheet) has one row per
// construction draw; this is which Use each row's EPC payment (Solar + Roof + ESS uses $) estimates. Applied
// when the reviewer accepts those rows of a model parse.
export const projectManagementModelMilestoneUses: { [modelMilestone: string]: string } = {
  'LNTP': 'm0', 'FNTP': 'm1', 'M2': 'm2', 'M3': 'm3', 'Mechanical Completion': 'm4', 'Substantial Completion': 'm5', 'Final Completion': 'm6',
};

// The document slot that holds the executed EPC contract: RIC's "Executed EPC Contract", required by its
// EPC Contract task. Its upload is what prices the M0–M6 Uses (server/projectManagementEpcContract.ts), and
// the Budget tab opens it beside them.
export const projectManagementEpcContractDocType = 'epcContract';

// How the EPC contract price is paid out across the M0–M6 milestone payments (Uses) when the agreement's
// own schedule cannot be read with confidence. Verified against the RIC tracker: every project followed
// this schedule against "Detailed Build Cost on EPCA". Applied to the Uses' ESTIMATES when an executed EPC
// contract is uploaded, replacing the model's — the signed price beats the modeled one, and the row's
// History shows the change.
export const projectManagementEpcaPaymentSplit: { [milestoneKey: string]: number } = { m0: 0.1, m1: 0.2, m2: 0.3, m3: 0.1, m4: 0.1, m5: 0.1, m6: 0.1 };

// Funding Sources whose ESTIMATE the financial model sets, by the model metric that carries it (the
// BTA Model sheet's labeled cells: Base Project Cost ($) I16, ITC Amount ($) I24, Net ITC Sale Proceeds ($)
// I28; the DevShare Model's Tax Equity Contribution ($) I20 and Realty Income Investment ($) I22). Applied
// when the reviewer accepts that row; a row the project is missing is created with the party the RIC import
// would have named. Actuals are never touched.
// `unlessAccepted`: a DevShare deal's Base Project Cost is the all-in cost, which Realty Income and tax
// equity share, so when the reviewer also accepts Realty Income's own figure, that is the Project Payment.
export const projectManagementModelFundingSources: { [metricName: string]: { type: ProjectManagementFundingSourceType, party: string, unlessAccepted?: string } } = {
  'Base Project Cost': { type: 'debt', party: 'Realty Income', unlessAccepted: 'Realty Income Investment' },
  'Realty Income Investment': { type: 'debt', party: 'Realty Income' },
  'Gross ITC': { type: 'grossItcRevenue', party: 'Open-Market ITC' },
  'Net ITC Revenue': { type: 'netItcRevenue', party: 'Open-Market ITC' },
  'Tax Equity Contribution': { type: 'taxEquity', party: 'Tax Equity Investor' },
};

// Which inflow milestones roll up into a funding source's estimated/actual totals, by source type.
// RIC debt (Realty Income) = the three work orders only — the Equipment-Only WO is deliberately excluded
// (add 'equipmentWo' to this array to include it). A type with no entry rolls up ALL of its inflows.
export const projectManagementFundingRollupKeys: { [type: string]: string[] } = {
  debt: ['lntp', 'fntp', 'wo3'],
};

/**
 * Estimated/actual totals for one funding source, derived from the cash ledger so they can't drift when a
 * work order is edited. Estimated prefers the source's own stored figure (RIC tax equity carries the GROSS
 * ITC from the tracker, which is deliberately not the sum of its inflows); otherwise it rolls up the
 * source's inflows. Actual always rolls up what was actually received. Undefined when there's nothing.
 * @param source - the funding source to total
 * @param cashEvents - the project's cash events
 */
export const projectManagementFundingSourceTotals = (
  source: ProjectManagementFundingSource,
  cashEvents: ProjectManagementCashEvent[],
): { estimated?: number, actual?: number } => {
  const keys = projectManagementFundingRollupKeys[source.type];
  const linked = cashEvents.filter((c) => c.direction === 'in' && c.fundingSourceId === source._id
    && (!keys || keys.includes(c.milestoneKey || '')));
  const sum = (field: 'estimatedAmount' | 'actualAmount') => {
    const values = linked.map((c) => c[field]).filter((v): v is number => typeof v === 'number');
    return values.length ? values.reduce((acc, v) => acc + v, 0) : undefined;
  };
  return { estimated: source.estimatedAmount ?? sum('estimatedAmount'), actual: sum('actualAmount') };
};

// The EPC construction payments (money-out) that the funder draws are netted against.
export const projectManagementEpcPaymentKeys = ['m0', 'm1', 'm2', 'm3', 'm4', 'm5', 'm6'];

/**
 * Luminia's GROSS fee on a project: the work-order draws in, minus the EPC construction payments out.
 * The inflow side reuses projectManagementFundingRollupKeys.debt (LNTP / FNTP / Substantial) so this and
 * the Debt funding-source total can never disagree about which work orders count — change it in one place.
 * Change orders and purchase orders eat into this figure but are NOT subtracted here; they are shown as
 * their own sections.
 * @param cashEvents - the project's cash events
 * @returns estimated and actual gross fee
 */
export const projectManagementGrossFees = (cashEvents: ProjectManagementCashEvent[]): { estimated: number, actual?: number } => {
  const inKeys = projectManagementFundingRollupKeys.debt || [];
  const inEvents = cashEvents.filter((c) => c.direction === 'in' && inKeys.includes(c.milestoneKey || ''));
  const outEvents = cashEvents.filter((c) => c.direction === 'out' && projectManagementEpcPaymentKeys.includes(c.milestoneKey || ''));
  const sum = (events: ProjectManagementCashEvent[], field: 'estimatedAmount' | 'actualAmount') =>
    events.reduce((acc, c) => acc + (c[field] || 0), 0);
  // The actual is only meaningful once EVERY participating draw and EPC payment has an actual — a partial
  // fee figure (money out, draws not yet received) reads as a scary negative, not information. Until then
  // it stays undefined and renders as an em dash.
  const participating = [...inEvents, ...outEvents];
  const complete = participating.length > 0 && participating.every((c) => typeof c.actualAmount === 'number');
  return {
    estimated: sum(inEvents, 'estimatedAmount') - sum(outEvents, 'estimatedAmount'),
    actual: complete ? sum(inEvents, 'actualAmount') - sum(outEvents, 'actualAmount') : undefined,
  };
};

/**
 * The invoice number to display. Finance viewers see Luminia's (QB-matching) number, annotated with the
 * vendor's original in parentheses when the two differ; everyone else sees only the vendor's own number.
 */
export const projectManagementInvoiceNumberLabel = (inv: Pick<ProjectManagementProjectInvoice, 'invoiceNumber' | 'luminiaInvoiceNumber'>, finance: boolean): string => {
  const epc = inv.invoiceNumber?.trim();
  if (!finance) return epc || '—';
  const lum = inv.luminiaInvoiceNumber?.trim();
  const primary = lum || epc;
  if (!primary) return '—';
  return (lum && epc && epc !== lum) ? `${lum} (${epc})` : primary;
};

/**
 * The name a project document should land under when somebody downloads it. Stored files carry a
 * content hash for a name, which tells the person who saved it nothing: this gives them the project and
 * the document instead, so a folder of downloads stays sortable and self-explanatory.
 *
 * The document's own label, NOT the task that asks for it: one task commonly delivers several documents,
 * and naming them all after the task makes a folder of downloads indistinguishable.
 * @param project - the owning project, for its permanent Luminia id
 * @param label - the document's label (the slot name)
 * @param file - the stored file, for its extension
 * @param suffix - appended before the extension (version history uses it to keep versions apart)
 * @returns a filename, or undefined when there is nothing better than what the browser would pick
 */
/** The longest a downloaded document name may be, extension included. */
export const projectManagementDocumentFileNameLimit = 255;

export const projectManagementDocumentFileName = (
  project: Pick<ProjectManagementProject, 'luminiaId' | 'externalId'> | undefined,
  label: string | undefined,
  file: Pick<FileMeta, 'name' | 'url'> | undefined,
  suffix?: string,
): string | undefined => {
  if (!file) return undefined;
  // Characters a filesystem would reject, plus runs of whitespace a label often carries from a template.
  const clean = (value: string) => value.replace(/[/\\:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
  const id = project?.luminiaId?.trim() || project?.externalId?.trim() || '';
  const stem = [id, clean(label || '')].filter(Boolean).join(' ') + (suffix ? ` ${clean(suffix)}` : '');
  if (!stem.trim()) return undefined;
  const source = file.name || file.url || '';
  const extension = source.includes('.') ? source.slice(source.lastIndexOf('.') + 1).split(/[?#]/)[0] : '';
  // 255 characters is the ceiling every filesystem in play agrees on; trim the stem, never the extension.
  const room = projectManagementDocumentFileNameLimit - (extension ? extension.length + 1 : 0);
  const trimmed = stem.length > room ? stem.slice(0, room).trimEnd() : stem;
  return extension ? `${trimmed}.${extension}` : trimmed;
};

/** The parameter names the fee formulas read — the pipeline publishes only these to keep its payload small. */
export const projectManagementTotalFeesParamNames = ['PV System Size', 'Tenant Billing per W', 'Luminia Dev Fee Percent', 'Solar Build Cost per W', 'Contingency', 'Base Project Cost'];

// Parameters that NAME Luminia's fee — the rate, the amount it works out to, and the per-watt upfront.
// They come off the financial model parse, not the lifecycle's seeds, so there is nothing to declare them
// on; matching by name catches one however it arrived, including hand-entered. Without the rate the fee
// cannot be recomputed from the figures that remain, which is what lets the bulk grid keep them.
export const projectManagementLuminiaFeeParamNames = ['Luminia Dev Fee', 'Luminia Dev Fee Percent', 'Luminia Upfront Fee per W'];

/** Everything withheld from a non-fee viewer on a PROJECT: the fees named outright, and the inputs the
 *  fee formulas read, which would otherwise let the figure be derived from the project's own economics. */
export const projectManagementFeeRestrictedParamNames = [
  ...new Set([...projectManagementTotalFeesParamNames, ...projectManagementLuminiaFeeParamNames]),
];

type FeeParameter = Pick<ProjectManagementParameter, 'name' | 'estimatedValue'>;
type FeeCashEvent = Pick<ProjectManagementCashEvent, 'direction' | 'milestoneKey' | 'estimatedAmount' | 'actualAmount' | 'changeOrder' | 'estimateSource' | 'label' | 'devFeeShare'>;

/** A parameter's numeric ESTIMATE — every fee formula runs on estimates. */
const estimatedParam = (parameters: FeeParameter[], name: string) => {
  const v = parameters.find((x) => x.name === name)?.estimatedValue;
  return typeof v === 'number' ? v : undefined;
};

/**
 * The EPC agreement's price, read back from the seven M0–M6 Uses it was spread across (applyEpcContractPayments
 * stamps each `estimateSource: 'epcContract'`). Undefined until every one of the seven carries the stamp — before
 * that the Uses hold model or hand figures, not the contract.
 */
export const projectManagementEpcaContractPrice = (cashEvents: FeeCashEvent[]): number | undefined => {
  const contract = cashEvents.filter((c) => c.direction === 'out' && c.estimateSource === 'epcContract' && projectManagementEpcPaymentKeys.includes(c.milestoneKey || ''));
  const keys = new Set(contract.map((c) => c.milestoneKey));
  if (projectManagementEpcPaymentKeys.some((k) => !keys.has(k))) return undefined;
  return contract.reduce((acc, c) => acc + (c.estimatedAmount || 0), 0);
};

export interface ProjectManagementAncillaryFees {
  /** (Contingency % ÷ 100) × PV System Size × Solar Build Cost per W × 1000; undefined if any estimate is missing. */
  contingency?: number;
  /** PV System Size × Commissioning per W × 1000; undefined if either estimate is missing. */
  commissioning?: number;
  /** Base Project Cost − EPCA price: what the partner pays over what the build costs (Jim's "Buyout Savings").
   *  Undefined until both are known; negative when the agreement costs more than the payment. */
  buyoutSavings?: number;
  baseProjectCost?: number;
  epcaPrice?: number;
}

/** The Ancillary Fees table's figures — estimates only, none of them stored. 'Contingency' is percent points. */
export const projectManagementAncillaryFees = (parameters: FeeParameter[], cashEvents: FeeCashEvent[]): ProjectManagementAncillaryFees => {
  const pvSystemSize = estimatedParam(parameters, 'PV System Size');
  const solarBuildCostPerW = estimatedParam(parameters, 'Solar Build Cost per W');
  const contingencyPercent = estimatedParam(parameters, 'Contingency');
  const contingency = contingencyPercent != null && pvSystemSize != null && solarBuildCostPerW != null
    ? (contingencyPercent / 100) * pvSystemSize * solarBuildCostPerW * 1000
    : undefined;
  const commissioningPerW = estimatedParam(parameters, 'Commissioning per W');
  const commissioning = commissioningPerW != null && pvSystemSize != null ? pvSystemSize * commissioningPerW * 1000 : undefined;
  const baseProjectCost = estimatedParam(parameters, 'Base Project Cost');
  const epcaPrice = projectManagementEpcaContractPrice(cashEvents);
  const buyoutSavings = baseProjectCost != null && epcaPrice != null ? baseProjectCost - epcaPrice : undefined;
  return { contingency, commissioning, buyoutSavings, baseProjectCost, epcaPrice };
};

/** How accepted change orders are absorbed: Contingency first, then Buyout Savings, the rest off the dev fee. */
export interface ProjectManagementFeeWaterfall {
  /** Accepted change orders — the only ones with a cash event (changeOrder: true). */
  changeOrders: number;
  contingencyUsed: number;
  buyoutSavingsUsed: number;
  /** What neither cushion absorbed. */
  devFeeReduction: number;
}

/** What a banner's Fees tile needs, however the vertical arrives at the figure. */
export interface ProjectManagementFeeTotal {
  estimated: number;
  actual?: number;
}

/**
 * The Fees of a vertical whose fees are Uses of its fund model rather than Luminia's own margin: the total
 * of the money-out events whose codes the lifecycle names (finance.feeUseCodes).
 *
 * Read off the ledger rather than from parameters, because that is where the fund model puts them — the
 * same rows the Budget tab's Uses table lists, so the tile and the table cannot disagree.
 * @param cashEvents - the project's cash events
 * @param codes - the milestoneKeys that are fees, from the lifecycle
 */
export const projectManagementUseFees = (
  cashEvents: { direction?: string, milestoneKey?: string, estimatedAmount?: number, actualAmount?: number, active?: boolean }[],
  codes: string[],
): ProjectManagementFeeTotal => {
  const fees = cashEvents.filter((c) => c.direction === 'out' && c.milestoneKey && codes.includes(c.milestoneKey) && c.active !== false);
  const total = (field: 'estimatedAmount' | 'actualAmount') => fees.reduce((sum, c) => sum + (c[field] || 0), 0);
  return { estimated: total('estimatedAmount'), actual: total('actualAmount') };
};

/** The DEFAULT share of the dev fee each work-order draw releases once it books money, in draw order. A
 *  project overrides it per draw with cashEvent.devFeeShare, the three set together so they total 100%. */
export const projectManagementDevFeeReleaseSplit: { [milestoneKey: string]: number } = { 'lntp': 0.2, 'fntp': 0.65, 'wo3': 0.15 };
/** The share of the dev fee a draw releases — the project's own over the default — or nothing for a row that is not a draw. */
export const projectManagementDevFeeShare = (c: Pick<ProjectManagementCashEvent, 'milestoneKey' | 'devFeeShare'>): number | undefined => {
  if (!c.milestoneKey || !(c.milestoneKey in projectManagementDevFeeReleaseSplit)) return undefined;
  return typeof c.devFeeShare === 'number' ? c.devFeeShare : projectManagementDevFeeReleaseSplit[c.milestoneKey];
};

/** One work-order draw's slice of the dev fee — a row of the Luminia Fees table. */
export interface ProjectManagementDevFeeDraw {
  milestoneKey: string;
  /** The draw's own cash-event label ('WO #1 – LNTP'); absent when the project has no such event. */
  label?: string;
  /** Its release share of the dev fee (0.2 = 20%) — the project's own, else the default. */
  share: number;
  /** The share of the modeled dev fee, less whatever part of the change-order reduction landed on this draw;
   *  undefined while the dev fee cannot be computed. */
  estimated?: number;
  /** The draw has money in (a positive actual on its cash event). */
  drawn: boolean;
  /** The estimate once the draw has money in; 0 until then, undefined while there is no estimate. */
  actual?: number;
}

export interface ProjectManagementTotalFees {
  /** (Luminia Dev Fee % ÷ 100) × PV System Size × Solar Build Cost per W × 1000 — the modeled dev fee, before
   *  change orders; undefined if any estimate is missing. */
  devFeesEstimated?: number;
  /** devFeesEstimated less the change orders left over once Contingency and Buyout Savings are spent. */
  devFeesNet?: number;
  /** Tenant Billing per W × PV System Size × 1000; undefined if either estimate is missing. */
  tenantBillingEstimated?: number;
  ancillary: ProjectManagementAncillaryFees;
  waterfall: ProjectManagementFeeWaterfall;
  /** Sum of the Luminia Fees table's Estimated column (net dev fee + tenant billing). */
  estimated: number;
  /** The net dev fee released as the work-order draws book actuals — the sum of `draws`' actuals.
   *  Undefined when there is no dev-fee estimate to release. */
  actual?: number;
  /** The dev fee split across the LNTP / FNTP / Substantial draws — always all three, so the table keeps its rows. */
  draws: ProjectManagementDevFeeDraw[];
}

/**
 * Luminia's Total Fees for one project — the single source for the Budget tab's fee tables, its banner tile and
 * the pipeline Finances card, so the per-project figure and the portfolio sum can never disagree. All inputs are
 * ESTIMATED parameter values; percents are stored as points (15 = 15%).
 * @param parameters - the project's parameters (only projectManagementTotalFeesParamNames need be present)
 * @param cashEvents - the project's cash events (direction/milestoneKey/amounts/changeOrder/estimateSource)
 */
export const projectManagementTotalFees = (parameters: FeeParameter[], cashEvents: FeeCashEvent[]): ProjectManagementTotalFees => {
  const pvSystemSize = estimatedParam(parameters, 'PV System Size');
  const tenantBillingPerW = estimatedParam(parameters, 'Tenant Billing per W');
  const tenantBillingEstimated = tenantBillingPerW != null && pvSystemSize != null ? tenantBillingPerW * pvSystemSize * 1000 : undefined;
  const devFeePercent = estimatedParam(parameters, 'Luminia Dev Fee Percent');
  const solarBuildCostPerW = estimatedParam(parameters, 'Solar Build Cost per W');
  const devFeesEstimated = devFeePercent != null && pvSystemSize != null && solarBuildCostPerW != null
    ? (devFeePercent / 100) * pvSystemSize * solarBuildCostPerW * 1000
    : undefined;
  // Accepted change orders draw the cushions down in order — Contingency, then Buyout Savings — and only what
  // is left comes off the dev fee, so the fee figures show a change order's effect the moment it is accepted.
  const ancillary = projectManagementAncillaryFees(parameters, cashEvents);
  const changeOrders = cashEvents.reduce((acc, c) => acc + (c.direction === 'out' && c.changeOrder ? (c.estimatedAmount || 0) : 0), 0);
  const contingencyUsed = Math.min(changeOrders, Math.max(ancillary.contingency || 0, 0));
  const buyoutSavingsUsed = Math.min(changeOrders - contingencyUsed, Math.max(ancillary.buyoutSavings || 0, 0));
  const devFeeReduction = changeOrders - contingencyUsed - buyoutSavingsUsed;
  const devFeesNet = devFeesEstimated != null ? devFeesEstimated - devFeeReduction : undefined;
  // A draw counts once money has come in. QuickBooks books an invoiced-but-unpaid draw as an actual of 0
  // (total less balance), and 0 must read as "not drawn", not as "drawn for nothing".
  const woDrawn = (milestoneKey: string) =>
    cashEvents.some((c) => c.direction === 'in' && c.milestoneKey === milestoneKey && (c.actualAmount || 0) > 0);
  const draws: ProjectManagementDevFeeDraw[] = Object.entries(projectManagementDevFeeReleaseSplit).map(([milestoneKey, defaultShare]) => {
    const event = cashEvents.find((c) => c.direction === 'in' && c.milestoneKey === milestoneKey);
    const share = (event && projectManagementDevFeeShare(event)) ?? defaultShare;
    return {
      milestoneKey,
      'label': event?.label,
      share,
      'estimated': devFeesEstimated != null ? devFeesEstimated * share : undefined,
      'drawn': woDrawn(milestoneKey),
    };
  });
  // The reduction comes off the latest draw not yet received first, so money already in stays as it was
  // booked; once every undrawn slice is used up it falls back on the drawn ones, latest first. Anything past
  // the whole fee stays on the first slice taken, keeping the rows summing to devFeesNet.
  if (devFeesEstimated != null) {
    const order = [...draws].reverse().sort((a, b) => Number(a.drawn) - Number(b.drawn));
    let toReduce = devFeeReduction;
    for (const draw of order) {
      const taken = Math.min(toReduce, Math.max(draw.estimated || 0, 0));
      draw.estimated = (draw.estimated || 0) - taken;
      toReduce -= taken;
    }
    if (toReduce > 0) order[0].estimated = (order[0].estimated || 0) - toReduce;
    for (const draw of draws) draw.actual = draw.drawn ? draw.estimated : 0;
  }
  const actual = devFeesEstimated != null ? draws.reduce((acc, d) => acc + (d.actual || 0), 0) : undefined;
  return {
    devFeesEstimated, devFeesNet, tenantBillingEstimated, ancillary,
    waterfall: { changeOrders, contingencyUsed, buyoutSavingsUsed, devFeeReduction },
    estimated: (devFeesNet || 0) + (tenantBillingEstimated || 0), actual, draws,
  };
};

/**
 * TEMPORARY (2026-10-28, expected to be reverted): a Complete project's ACTUAL fee is its cash margin —
 * the Sources' actuals less the Uses' actuals — instead of the dev fee released by the work-order draws.
 * Applied wherever RIC's fee actual is shown on the pipeline (Table column, Total Fees tile) and on the
 * project page's Fees tile; the Budget tab's fee tables are untouched. Reverting is reverting its commit.
 * @param fees - the project's fee figures as projectManagementTotalFees reports them
 * @param project - the project, for its status
 * @param cashEvents - the project's cash events
 * @returns the fees, with the actual replaced for a Complete project
 */
export const projectManagementCompleteProjectFees = <T extends { actual?: number }>(
  fees: T,
  project: { status?: ProjectManagementProjectStatus },
  cashEvents: ProjectManagementCashEvent[],
): T => (projectManagementProjectStatus(project) === 'complete'
    ? { ...fees, actual: projectManagementFinancialSummary(cashEvents).netActual }
    : fees);

/** Formats a dollar amount for display (rounded, thousands-separated); em dash when absent. */
// Whole dollars by default (the Budget tab's figures are large); pass cents=true where the fractional
// part matters — e.g. allocating an invoice to the exact cent.
export const projectManagementMoney = (n?: number, cents?: boolean): string => (
  n == null ? '—'
    : cents ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `$${Math.round(n).toLocaleString()}`
);

// Typical parameter units offered in the picker (free-form entry is also allowed).
export const projectManagementParameterUnits = ['$', '%', '$/W', '$/kWh', 'kWh', 'MWh', 'kW DC', 'kW AC', 'V', 'years'] as const;

/** True for the numeric parameter types (variance + unit formatting only apply to these). */
export const projectManagementParameterIsNumeric = (type?: ProjectManagementParameterType): boolean =>
  type == null || type === 'integer' || type === 'number';

/** The local calendar day a date falls on, as a comparable number. */
// UTC components, because every date this compares is a calendar day stored as midnight UTC. Read in
// local components the client (a person's zone) and the server (prod is UTC) could disagree about which
// day a date falls on, so a task date the picker accepted would be rejected on save — or, west of UTC,
// a task a day past its milestone would read as the same day and pass.
export const projectManagementDayStart = (d: Date | string): number => {
  const x = new Date(d);
  return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
};

/**
 * Whether a task date breaks the rule that it must fall ON OR BEFORE its milestone's corresponding date.
 *
 * Compared by CALENDAR DAY, not by instant, and that matters in both directions: task dates are entered
 * as days (local midnight), while a milestone's actualDate is auto-stamped with a full `new Date()` when
 * its last task completes — so comparing instants would reject a same-day actual for a reason invisible
 * to the user, and a date typed as UTC midnight could read as the previous day west of UTC.
 *
 * False when either side is missing: a milestone with no date yet imposes no ceiling, which is the norm
 * (actualDate does not exist until the milestone completes). Shared by the server check and the card's
 * client-side hint so the two cannot drift.
 */
export const projectManagementTaskDateAfterMilestone = (
  taskDate?: Date | null, milestoneDate?: Date | null,
): boolean => !!taskDate && !!milestoneDate
  && projectManagementDayStart(taskDate) > projectManagementDayStart(milestoneDate);

/**
 * Whether a task counts as ASSIGNED TO a stakeholder: explicitly owned by them. Task assignment is manual
 * and decoupled from roles — a stakeholder's roles no longer auto-assign matching tasks.
 *
 * The predicate twin of projectManagementMemberOwnedQuery (server/projectManagementAccess.ts) for tasks
 * (roleField 'role') — a query cannot be evaluated against an in-memory document, so the two shapes
 * coexist. Change BOTH together. (Milestones still fall back to ownerRole; that lives only in the query.)
 */
export const projectManagementTaskOwnedBy = (
  task: Pick<ProjectManagementTask, 'ownerStakeholderIds'>,
  stakeholder: Pick<ProjectManagementStakeholder, '_id'>,
): boolean => (task.ownerStakeholderIds || []).includes(stakeholder._id);

/**
 * The docTypes a stakeholder may see: the union of requiredDocSlots across the tasks assigned to them.
 * Mirrors projectManagementMemberDocTypes, which asks the database the same question for the member
 * publication.
 */
export const projectManagementStakeholderDocTypes = (
  tasks: ProjectManagementTask[],
  stakeholder: Pick<ProjectManagementStakeholder, '_id'>,
): Set<string> => new Set(
  tasks.filter((t) => projectManagementTaskOwnedBy(t, stakeholder)).flatMap((t) => t.requiredDocSlots || []),
);

/** Formats a parameter value by its type & unit: strings verbatim; dates/datetimes localized; numbers get
 *  the unit ('$' prefixes, '%' suffixes, else trailing) — no unit → a plain number (never assumed dollars). */
export const projectManagementFormatValue = (value?: number | string | boolean | Date, unit?: string, type?: ProjectManagementParameterType): string => {
  if (value == null || value === '') return '—';
  if (type === 'boolean') return value ? strings.Yes() : strings.No();
  if (type === 'date' || type === 'datetime') {
    const d = value instanceof Date ? value : new Date(value as any);
    if (isNaN(d.getTime())) return String(value);
    // A 'date' parameter is a calendar day, stored as midnight UTC by the picker and every importer — read
    // in the viewer's zone it names the day before west of UTC. A 'datetime' is a real moment and stays
    // local. This also makes the SERVER render a date parameter the same way the client does, which the
    // bulk grid's column-values note further down had to work around.
    return type === 'date' ? formatCalendarDate(d) : d.toLocaleString();
  }
  if (type === 'string') return String(value);
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  const u = unit?.trim();
  // 2 dp suits costs/rates, but small non-zero values (e.g. a $/W rate of 0.000344) round to "0" at 2 dp —
  // give sub-1 magnitudes up to 6 dp so they stay visible. Trailing zeros are still dropped.
  const maxFractionDigits = num !== 0 && Math.abs(num) < 1 ? 6 : 2;
  const n = num.toLocaleString(undefined, { maximumFractionDigits: maxFractionDigits });
  if (!u) return n;
  if (u === '$') return `$${Math.round(num).toLocaleString()}`;
  if (u === '%') return `${n}%`;
  return `${n} ${u}`;
};

const parameterAsDate = (v?: number | string | boolean | Date): Date | undefined => {
  if (v == null || v === '' || typeof v === 'boolean') return undefined;
  const d = v instanceof Date ? v : new Date(v as any);
  return isNaN(d.getTime()) ? undefined : d;
};

/** Variance for a date/datetime parameter as a signed timedelta (e.g. '3 days', '-1 day 15 min'). */
export const projectManagementDateVariance = (estimated?: number | string | boolean | Date, actual?: number | string | boolean | Date, type?: ProjectManagementParameterType): string | undefined => {
  const e = parameterAsDate(estimated);
  const a = parameterAsDate(actual);
  if (!e || !a) return undefined;
  const ms = a.getTime() - e.getTime();
  const sign = ms < 0 ? '-' : '';
  const abs = Math.abs(ms);
  if (type === 'date') {
    const days = Math.round(abs / 86400000);
    return `${days ? sign : ''}${days} ${days === 1 ? 'day' : 'days'}`;  // no sign on a zero magnitude ('0 days', not '-0 days')
  }
  const d = Duration.fromMillis(abs).shiftTo('days', 'hours', 'minutes');
  const mins = Math.round(d.minutes);
  const parts = [
    d.days && `${d.days} ${d.days === 1 ? 'day' : 'days'}`,
    d.hours && `${d.hours} ${d.hours === 1 ? 'hr' : 'hrs'}`,
    mins && `${mins} min`,
  ].filter(Boolean);
  return parts.length ? `${sign}${parts.join(' ')}` : '0 min';  // sign only when there's a non-zero magnitude
};

/** Formatted variance for a parameter: number delta (with unit) for numerics, a timedelta for dates. */
export const projectManagementParameterVariance = (p: ProjectManagementParameter): string | undefined => {
  if (projectManagementParameterIsNumeric(p.type)) {
    return (typeof p.estimatedValue === 'number' && typeof p.actualValue === 'number')
      ? projectManagementFormatValue(p.actualValue - p.estimatedValue, p.unit, p.type) : undefined;
  }
  if (p.type === 'date' || p.type === 'datetime') return projectManagementDateVariance(p.estimatedValue, p.actualValue, p.type);
  return undefined;
};

/** Display label for a project's externalId: the lifecycle's override (e.g. RIC's "RIC #") or the generic label. */
export const projectManagementExternalIdLabel = (lifecycleKey?: string): string =>
  (lifecycleKey && getProjectManagementLifecycle(lifecycleKey)?.externalIdLabel) || strings.ExternalId();

/** Display label for a parameter's value type. */
export const projectManagementParameterTypeLabel = (type?: ProjectManagementParameterType): string => {
  switch (type) {
    case 'string': return strings.ParameterTypeText();
    case 'integer': return strings.ParameterTypeInteger();
    case 'boolean': return strings.ParameterTypeBoolean();
    case 'date': return strings.ParameterTypeDate();
    case 'datetime': return strings.ParameterTypeDateTime();
    default: return strings.ParameterTypeNumber();
  }
};

/** Display label for a funding-source type. */
export const projectManagementFundingSourceTypeLabel = (type: ProjectManagementFundingSourceType): string => {
  switch (type) {
    case 'taxEquity': return strings.TaxEquity();
    case 'grossItcRevenue': return strings.GrossItcRevenue();
    case 'netItcRevenue': return strings.NetItcRevenue();
    case 'loan': return strings.PrePaidLoan();
    case 'capitalAdvance': return strings.CapitalAdvance();
    case 'prepaidPpa': return strings.PrepaidPpa();
    case 'sgipIncentive': return strings.SgipIncentive();
    default: return strings.Debt();
  }
};

// --- Change-order roll-up (pure; UI + reporting): count plus open/accepted totals. ---
export interface ProjectManagementChangeOrderSummary {
  count: number;               // all non-rejected change orders
  totalValue: number;          // Σ value of non-rejected change orders
  acceptedValue: number;       // Σ value of accepted change orders
}

export const projectManagementChangeOrderSummary = (
  changeOrders: ProjectManagementChangeOrder[],
): ProjectManagementChangeOrderSummary => {
  const live = changeOrders.filter((c) => c.status !== 'rejected');
  const totalValue = live.reduce((acc, c) => acc + (c.value || 0), 0);
  const acceptedValue = live.filter((c) => c.status === 'accepted').reduce((acc, c) => acc + (c.value || 0), 0);
  return { count: live.length, totalValue, acceptedValue };
};

/**
 * The change-order value above which this lifecycle wants Investment Committee sign-off. The contract
 * base is the estimated money-out EXCLUDING change-order lines — the original contract price, so a
 * project that has already absorbed change orders cannot quietly raise its own threshold.
 * @param approvalPct - the lifecycle's threshold, as a percentage of the contract base
 * @param cashEvents - the project's cash events
 * @returns the cutoff amount, or undefined where the vertical sets no threshold or nothing is estimated yet
 */
export const projectManagementChangeOrderIcThreshold = (
  approvalPct: number | undefined,
  cashEvents: ProjectManagementCashEvent[],
): number | undefined => {
  if (!approvalPct) return undefined;
  const contractBase = cashEvents.filter((c) => c.direction === 'out' && !c.changeOrder).reduce((acc, c) => acc + (c.estimatedAmount || 0), 0);
  return contractBase > 0 ? (contractBase * approvalPct) / 100 : undefined;
};

/**
 * Whether a change order exceeds that threshold. Display-only accountability: the IC is not a modeled
 * approver, so nothing here blocks accepting one.
 */
export const projectManagementChangeOrderNeedsIc = (
  value: number | undefined,
  approvalPct: number | undefined,
  cashEvents: ProjectManagementCashEvent[],
): boolean => {
  const threshold = projectManagementChangeOrderIcThreshold(approvalPct, cashEvents);
  return !!value && threshold !== undefined && value > threshold;
};

/** Resolves an item's assignees, in the order the stakeholder list gives them so two renderings of the
 *  same task never disagree. Assignment is explicit/manual only — neither tasks nor milestones fall back
 *  to a role. */
export const projectManagementOwnersOf = (
  item: { ownerStakeholderIds?: string[] },
  stakeholders: ProjectManagementStakeholder[],
): ProjectManagementStakeholder[] =>
  (item.ownerStakeholderIds?.length ? stakeholders.filter((s) => item.ownerStakeholderIds!.includes(s._id)) : []);

// --- One workspace row: a project enriched with its milestones + cash summary. Feeds every view
//     (board / table / timeline / map / finances) from a single fetch. ---
export interface ProjectManagementPipelineRow {
  project: ProjectManagementProject;
  // Only populated where the view needs it (the member pipeline, for the 'Next Task' column); admins get [].
  tasks?: ProjectManagementTask[];
  milestones: ProjectManagementMilestone[];
  cash: ProjectManagementFinancialSummary;
  // Luminia's Total Fees (the Budget tab's stat) — admin pipeline only; members have no parameters published.
  // Either shape: RIC's full fee breakdown, or the plain total a vertical reads off its fund model's Uses.
  // Every reader of this field wants the two figures both of them carry.
  fees?: ProjectManagementTotalFees | ProjectManagementFeeTotal;
}

// --- Roll-up of the workspace's CURRENT row set (lifecycle-filtered, searched) for the summary strip —
//     scoped to what's rendered below it, unlike the home dashboard's cross-lifecycle totals. ---
export interface ProjectManagementPipelineSummary {
  /** How many projects are in view — every status, including the ones the banner's size split leaves out. */
  totalProjects: number;
  /**
   * Installed size by status — what the projects in view CARRY, not how many there are.
   * Read from the project spine (capacityKwDc / storageKwh) rather than the 'PV System Size' and
   * 'BESS Capacity (kWh)' parameters the project banner prefers: those are fee-restricted and reach only
   * fee viewers on this publication, so summing them would hand different roles different totals.
   */
  capacityByStatus: { [k in ProjectManagementProjectStatus]: { kwDc: number, kwh: number } };
  /** How many projects sit in each status — the count beside the size in the banner's split. */
  countByStatus: { [k in ProjectManagementProjectStatus]: number };
  cash: ProjectManagementFinancialSummary;
  /** Portfolio Total Fees — the sum of each project's Budget-tab "Total fees" stat (projectManagementTotalFees). */
  fees: { estimated: number, actual: number };
}

/**
 * Review roll-up across a pipeline's rows, for verticals whose milestones are reviewed. Average approval
 * time is measured readyAt → reviewedAt over approved milestones only, so a milestone still sitting with
 * the reviewer never flatters the number.
 * @param rows - the pipeline rows in view (the filter applies, like every other tile)
 * @returns counts plus the average approval time in days, or undefined when nothing has been approved yet
 */
export const projectManagementReviewSummary = (rows: ProjectManagementPipelineRow[]): { awaiting: number, deficiencies: number, avgApprovalDays?: number } => {
  const milestones = rows.flatMap((r) => r.milestones || []);
  const approved = milestones.filter((m) => m.reviewStatus === 'approved' && m.readyAt && m.reviewedAt);
  const totalMs = approved.reduce((n, m) => n + (new Date(m.reviewedAt as Date).getTime() - new Date(m.readyAt as Date).getTime()), 0);
  return {
    awaiting: milestones.filter((m) => m.reviewStatus === 'readyForReview').length,
    deficiencies: milestones.filter((m) => m.reviewStatus === 'deficiency').length,
    avgApprovalDays: approved.length ? totalMs / approved.length / 86400000 : undefined,
  };
};

export const projectManagementPipelineSummary = (rows: ProjectManagementPipelineRow[]): ProjectManagementPipelineSummary => {
  const cash = rows.reduce((acc, r) => ({
    inEstimated: acc.inEstimated + r.cash.inEstimated, inActual: acc.inActual + r.cash.inActual,
    outEstimated: acc.outEstimated + r.cash.outEstimated, outActual: acc.outActual + r.cash.outActual,
    netEstimated: acc.netEstimated + r.cash.netEstimated, netActual: acc.netActual + r.cash.netActual,
    outChangeOrderEstimated: acc.outChangeOrderEstimated + r.cash.outChangeOrderEstimated,
    outChangeOrderActual: acc.outChangeOrderActual + r.cash.outChangeOrderActual,
  }), {
    inEstimated: 0, inActual: 0, outEstimated: 0, outActual: 0, netEstimated: 0, netActual: 0,
    outChangeOrderEstimated: 0, outChangeOrderActual: 0,
  });
  // A project with no dev-fee estimate has an undefined actual (nothing to release) and contributes 0.
  const fees = rows.reduce(
    (acc, r) => ({ estimated: acc.estimated + (r.fees?.estimated || 0), actual: acc.actual + (r.fees?.actual || 0) }),
    { estimated: 0, actual: 0 },
  );
  const capacityByStatus = projectManagementProjectStatuses.reduce(
    (acc, st) => ({ ...acc, [st]: { kwDc: 0, kwh: 0 } }),
    {} as { [k in ProjectManagementProjectStatus]: { kwDc: number, kwh: number } },
  );
  const countByStatus = projectManagementProjectStatuses.reduce(
    (acc, st) => ({ ...acc, [st]: 0 }),
    {} as { [k in ProjectManagementProjectStatus]: number },
  );
  // Only a positive number is a size, matching the project banner: a missing or zero field contributes
  // nothing, so a portfolio with no storage reports none rather than a column of zeroes. The COUNT has no
  // such rule — a project with no size entered is still a project, and is counted in its status.
  const size = (v?: number) => (typeof v === 'number' && v > 0 ? v : 0);
  for (const r of rows) {
    const status = projectManagementProjectStatus(r.project);
    countByStatus[status] += 1;
    const carried = capacityByStatus[status];
    carried.kwDc += size(r.project.capacityKwDc);
    carried.kwh += size(r.project.storageKwh);
  }
  return { totalProjects: rows.length, capacityByStatus, countByStatus, cash, fees };
};

// --- Result of a data-room export: the zip's URL plus what was included/dropped (no silent truncation). ---
export interface ProjectManagementDataRoomResult {
  url: string;
  name: string;
  token?: string;    // signed capability token for the /nfs auth gate (see fileHref)
  files: number;
  skipped: number;   // docs whose current version was missing on disk or (in approved-only mode) not approved
}

// --- Result of the RIC portfolio-tracker import (a DevTool seed). ---
export interface ProjectManagementRicImportResult {
  rows: number;
  skipped: number;          // rows with no WO #1 (col BI) — not yet funded, so no PM project is made
  created: number;
  updated: number;
  milestoneDates: number;
  taskDates: number;        // task estimatedDates filled from the "Estimated Dates and Modeling" sheet
  cashEvents: number;
  milestonesCompleted: number;  // completed by the post-import reconcile (paid taskless draws, all-tasks-done)
  errors: string[];
}

// Result of seeding Residential (TPO, non-SGIP) projects from the Residential Pipeline Tracker xlsx.
export interface ProjectManagementResTpoImportResult {
  rows: number;
  skipped: number;      // rows with no ClickUp ID, or cancelled rows carrying no project data
  created: number;
  updated: number;
  parameters: number;        // parameter values written from tracker columns
  parametersKept: number;    // tracker values NOT written: the parameter tracks no actual and already had one
  parametersMissing: number; // tracker values dropped: the project carries no parameter of that name
  milestones: number;   // milestone review states / dates seeded from the Payment Milestones sheet
  utilities: number;    // distinct utilities the tracker named, each resolved to a registry row
  // Partner tasks handed to the dealer's people because this run named the dealer. A new project's tasks
  // are assigned as it is created, so this counts the re-imports — and reads 0 until somebody has put the
  // dealer's people on the project.
  tasksAssigned: number;
  errors: string[];
}

// Result of wiping the org's RIC projects (the Meteor equivalent of miscl/scripts/wipeRicProjects.js).
export interface ProjectManagementWipeRicResult {
  projects: number;                 // RIC projects deleted
  childRecords: number;             // per-project child rows deleted (milestones/tasks/docs/cash/…)
  portfolios: number;               // importer-created portfolios swept once left empty
  owners: number;
  epcs: number;
  utilities: number;                // owner/epc/utility registry rows swept once left unreferenced
  stakeholderProfiles: number;      // profiles swept once no membership references them
  utilityAccountsUnlinked: number;  // utility accounts detached (never deleted — credentials are org assets)
  inventorySitesUnlinked: number;   // inventory sites detached from the wiped projects (never deleted)
}

// --- Method declarations ---
export const projectManagementListProjects = DeclareMethod<{ lifecycleKey?: string }, ProjectManagementProject[]>('projectManagementListProjects');
/** The lifecycle keys this user actually has projects in — what the workspace's Vertical selector offers. */
export const projectManagementLifecyclesInUse = DeclareMethod<void, string[]>('projectManagementLifecyclesInUse');
export const projectManagementCreateProject = DeclareMethod<Partial<ProjectManagementProject>, string>('projectManagementCreateProject');
/** An edit of the project spine: a field set to null is cleared (the Edit modal sends null for an emptied input). */
export type ProjectManagementProjectPatch = { projectManagementProjectId: string } & { [K in keyof ProjectManagementProject]?: ProjectManagementProject[K] | null };
export const projectManagementUpdateProject = DeclareMethod<ProjectManagementProjectPatch>('projectManagementUpdateProject');
export const projectManagementDeleteProject = DeclareMethod<{ projectManagementProjectId: string }>('projectManagementDeleteProject');
export const projectManagementSetTaskStatus = DeclareMethod<{ taskId: string, status: 'open' | 'done' }>('projectManagementSetTaskStatus');
export const projectManagementCreateDocument = DeclareMethod<{ projectManagementProjectId: string, docType: string, label: string }, string>('projectManagementCreateDocument');
export const projectManagementAddDocumentVersion = DeclareMethod<{ projectManagementDocumentId: string, file: FileMeta, changeNote?: string }>('projectManagementAddDocumentVersion');
export const projectManagementSetVersionApproval = DeclareMethod<{ versionId: string, status: 'approved' | 'rejected' | 'pending', rejectedReason?: string }>('projectManagementSetVersionApproval');
export const projectManagementSendDocumentDigest = DeclareMethod<{ day?: string }, ProjectManagementDocumentDigestResult>('projectManagementSendDocumentDigest');
export const projectManagementRevertVersion = DeclareMethod<{ projectManagementDocumentId: string, versionId: string }>('projectManagementRevertVersion');
export const projectManagementSetMilestoneDate = DeclareMethod<{ milestoneId: string, field: 'plannedDate' | 'actualDate', date: Date | null }>('projectManagementSetMilestoneDate');
/** Sets or clears (date: null) one of a task's reported dates. Rejects a date after its milestone's. */
export const projectManagementSetTaskDate = DeclareMethod<{ taskId: string, field: 'estimatedDate' | 'actualDate', date: Date | null }>('projectManagementSetTaskDate');

// Create (no _id) or update (with _id) a milestone. Create slots it chronologically by plannedDate.
export const projectManagementSaveMilestone = DeclareMethod<{ _id?: string, projectManagementProjectId?: string, label: string, phase?: string, ownerRole?: string, plannedDate?: Date | null }, string>('projectManagementSaveMilestone');
// Delete a milestone; every task under it MUST be moved to another milestone via taskMoves first.
export const projectManagementDeleteMilestone = DeclareMethod<{ milestoneId: string, taskMoves: { taskId: string, milestoneId: string }[] }>('projectManagementDeleteMilestone');
// Create (no _id) or update (with _id) a task. Super-user only; milestone association is required.
export const projectManagementSaveTask = DeclareMethod<{ _id?: string, projectManagementProjectId?: string, label: string, description?: string, role: string, milestoneId: string }, string>('projectManagementSaveTask');
// Delete a task (super-user only, completed included): its parameters are unlinked, never deleted.
export const projectManagementDeleteTask = DeclareMethod<{ taskId: string }>('projectManagementDeleteTask');
// Create (no documentId) or rename a document slot on a task. Super-user only.
export const projectManagementSaveTaskDocument = DeclareMethod<{ taskId: string, documentId?: string, label: string }, string>('projectManagementSaveTaskDocument');
// Remove a document slot from a task (super-user only). Uploads are never destroyed: a versioned or
// still-referenced document is only detached (it resurfaces on the Documents tab); a version-less
// placeholder row is deleted outright.
export const projectManagementDeleteTaskDocument = DeclareMethod<{ taskId: string, documentId: string }>('projectManagementDeleteTaskDocument');
// Excuse a requirement from its task's completion test, or put it back. Each re-evaluates the task(s)
// affected, since either direction changes what the task is waiting on.
export const projectManagementSetParameterOptional = DeclareMethod<{ parameterId: string, optional: boolean }>('projectManagementSetParameterOptional');
export const projectManagementSetDocumentOptional = DeclareMethod<{ documentId: string, optional: boolean }>('projectManagementSetDocumentOptional');
export const projectManagementSaveFundingSource = DeclareMethod<Partial<ProjectManagementFundingSource> & { projectManagementProjectId: string }, string>('projectManagementSaveFundingSource');
export const projectManagementDeleteFundingSource = DeclareMethod<{ id: string }>('projectManagementDeleteFundingSource');
export const projectManagementSaveCashEvent = DeclareMethod<Partial<ProjectManagementCashEvent> & { projectManagementProjectId: string }, string>('projectManagementSaveCashEvent');
export const projectManagementSetDrawSplit = DeclareMethod<{ projectManagementProjectId: string, shares: { [milestoneKey: string]: number } }, void>('projectManagementSetDrawSplit');
// RIC: how the dev fee is shared across the three work-order draws, which must total 100%.
export const projectManagementSetDevFeeSplit = DeclareMethod<{ projectManagementProjectId: string, shares: { [milestoneKey: string]: number } }, void>('projectManagementSetDevFeeSplit');
// Residential: the dealer payments' shares of the System Purchase Price, which must total 100%.
export const projectManagementSetDealerSplit = DeclareMethod<{ projectManagementProjectId: string, shares: { [milestoneKey: string]: number } }, void>('projectManagementSetDealerSplit');
// The Finances view's export. Takes the project ids IN THE ORDER the view renders them (its filters and
// sort are the user's, and client-side); the money is re-summed server-side from the cash events, and the
// ids are re-scoped to what the caller may see. Returns a signed /nfs URL to the written workbook.
export const projectManagementFinancesExport = DeclareMethod<{ projectIds: string[] }, string>('projectManagementFinancesExport');
export const projectManagementDeleteCashEvent = DeclareMethod<{ id: string }>('projectManagementDeleteCashEvent');
/** `estimatedValue`/`actualValue`: a value sets it, `null` CLEARS it, `undefined` leaves it alone.
 *  (Before the bulk grid there was no way to clear one — $set drops undefined, so empty silently no-op'd.) */
export type ProjectManagementParameterValue = number | string | boolean | Date;
export const projectManagementSaveParameter = DeclareMethod<
  Omit<Partial<ProjectManagementParameter>, 'estimatedValue' | 'actualValue'>
  & { projectManagementProjectId: string, estimatedValue?: ProjectManagementParameterValue | null, actualValue?: ProjectManagementParameterValue | null },
  string
>('projectManagementSaveParameter');
export const projectManagementDeleteParameter = DeclareMethod<{ id: string }>('projectManagementDeleteParameter');
// --- Financial model: upload → parse → REVIEW → accept ---------------------------------------------
// One parsed metric, paired with what the project holds for it today so the review can show the change
// before anything is written. `currentEstimatedValue` is a snapshot taken at parse time (display only).
export interface ProjectManagementParsedModelRow {
  name: string;
  // The deterministic BTA parse yields dates and strings as well as figures; the LLM fallback is numbers only.
  value: ProjectManagementParameterValue;
  unit?: string;
  type: ProjectManagementParameterType;
  category?: ProjectManagementParameterCategory;
  source: 'model' | 'llm';            // deterministic parse vs the LLM fallback
  // Whether the parameter tracks an actual once this row lands. Set by a parser whose figures are all
  // forecasts the as-built confirms (Apollo); absent leaves the parameter's own setting alone.
  tracksActual?: boolean;
  currentEstimatedValue?: ProjectManagementParameterValue;
  exists: boolean;                    // whether a parameter of this name already exists on the project
  // Where the row lands on accept. Absent = the parameter of this name; set = that cash event's ESTIMATE
  // (the model's milestone disbursement rows estimate the M0–M6 Uses). A `label` has the event created
  // under it when the project has none: RIC's Uses are seeded placeholders and a missing one is an anomaly,
  // residential's fund uses exist only once a model names them.
  cashEvent?: { direction: 'in' | 'out'; milestoneKey: string; label?: string };
  // Or that funding source's ESTIMATE, matched by type and created with this party (and the note, the
  // label the model gives the row) when the project has none — residential's Sources & Uses tab.
  fundingSource?: { type: ProjectManagementFundingSourceType; party: string; note?: string };
}
/** Parses the uploaded model and RETURNS what it found. Writes nothing — see projectManagementApplyParsedModel. */
export const projectManagementParseModel = DeclareMethod<
  { projectManagementProjectId: string },
  { rows: ProjectManagementParsedModelRow[], milestones?: number, warnings?: string[], usedAi?: boolean }
>('projectManagementParseModel');
/** Commits the rows a human accepted in the review modal. */
export const projectManagementApplyParsedModel = DeclareMethod<
  { projectManagementProjectId: string, rows: ProjectManagementParsedModelRow[] },
  { applied: number }
>('projectManagementApplyParsedModel');
/** Uploads a file into the project's financialModel slot and approves it in the same call (super-user). */
export const projectManagementUploadFinancialModel = DeclareMethod<
  { projectManagementProjectId: string, file: FileMeta },
  { documentId: string }
>('projectManagementUploadFinancialModel');
// Save a MEMBERSHIP: attach a stakeholder profile to a project with per-project roles. The caller either
// names an existing `profileId`, or passes `profile` fields to find-or-create the org-level profile inline
// (the modal path — keeps its single-form UX).
export const projectManagementSaveStakeholder = DeclareMethod<{
  _id?: string;
  projectManagementProjectId: string;
  profileId?: string;
  profile?: Partial<ProjectManagementStakeholderProfile> & { name: string };
  notes?: string;              // per-project membership notes
}, string>('projectManagementSaveStakeholder');
export const projectManagementDeleteStakeholder = DeclareMethod<{ id: string }>('projectManagementDeleteStakeholder');
// Org-level stakeholder profiles (identity directory). Upsert matches by accountId → email → name in-org.
export const projectManagementSaveStakeholderProfile = DeclareMethod<Partial<ProjectManagementStakeholderProfile> & { name: string }, string>('projectManagementSaveStakeholderProfile');
export const projectManagementDeleteStakeholderProfile = DeclareMethod<{ id: string }>('projectManagementDeleteStakeholderProfile');

// --- Bulk stakeholder management (person-anchored): manage one profile's memberships and task assignments
//     across many projects at once. Data comes from methods (admin-only, on-demand) rather than reactive
//     publications — the volume (all org projects × tasks) is too big to keep live. ---
export interface ProjectManagementBulkProject {
  _id: string;
  name: string;
  portfolioId?: string;
  address?: string;
  state?: string;            // best-effort 2-letter code parsed from the address (no structured field exists)
  lifecycleKey: string;
  externalId?: string;
}
export interface ProjectManagementBulkTemplate { templateKey: string; label: string; role?: string; }  // role = the task bucket
export interface ProjectManagementStakeholderBulkData {
  profile?: ProjectManagementStakeholderProfile;
  projects: ProjectManagementBulkProject[];
  portfolios: { _id: string; name: string }[];
  memberships: { _id: string; projectManagementProjectId: string }[];
  templates: ProjectManagementBulkTemplate[];  // distinct task templates across the profile's member projects
}
/** One project's ownership of a given task template by the profile: the task row + whether it's owned. */
export interface ProjectManagementProfileTemplateCell {
  projectManagementProjectId: string;
  taskId: string;
  owned: boolean;
}

export const projectManagementStakeholderBulkData = DeclareMethod<{ profileId?: string }, ProjectManagementStakeholderBulkData>('projectManagementStakeholderBulkData');
export const projectManagementProfileTemplateOwnership = DeclareMethod<{ profileId: string; templateKey: string }, ProjectManagementProfileTemplateCell[]>('projectManagementProfileTemplateOwnership');
export const projectManagementStakeholderProfilesList = DeclareMethod<{ search?: string }, ProjectManagementStakeholderProfile[]>('projectManagementStakeholderProfilesList');
export const projectManagementSetProfileMemberships = DeclareMethod<{ profileId: string; addProjectIds?: string[]; removeProjectIds?: string[] }, { added: number; removed: number }>('projectManagementSetProfileMemberships');
export const projectManagementSetProfileTaskAssignments = DeclareMethod<{ profileId: string; templateKeys: string[]; assignProjectIds?: string[]; unassignProjectIds?: string[] }, { assigned: number; unassigned: number }>('projectManagementSetProfileTaskAssignments');
// A vertical's WHOLE task catalogue, for the Defaults tab: the bulk data above only knows the templates of
// projects the person already belongs to, and a default has to be declarable against a task nobody holds.
export const projectManagementLifecycleTemplates = DeclareMethod<{ lifecycleKey: string }, ProjectManagementBulkTemplate[]>('projectManagementLifecycleTemplates');
// Replaces this profile's defaults FOR ONE VERTICAL; the other verticals' entries are left alone.
export const projectManagementSetProfileDefaults = DeclareMethod<{ profileId: string; lifecycleKey: string; templateKeys: string[] }, void>('projectManagementSetProfileDefaults');
/** Who defaults to each task template of a vertical — the coverage view, derived from the profiles. */
export interface ProjectManagementDefaultCoverageRow { templateKey: string; label: string; role?: string; people: { profileId: string; name: string }[]; }
export const projectManagementDefaultCoverage = DeclareMethod<{ lifecycleKey: string }, ProjectManagementDefaultCoverageRow[]>('projectManagementDefaultCoverage');
// Applies this profile's defaults to projects that ALREADY exist, filling only tasks nobody owns.
export const projectManagementBackfillProfileDefaults = DeclareMethod<{ profileId: string; lifecycleKey: string }, { projects: number; tasks: number; memberships: number }>('projectManagementBackfillProfileDefaults');
export const projectManagementSavePortfolio = DeclareMethod<Partial<ProjectManagementPortfolio> & { name: string }, string>('projectManagementSavePortfolio');
// Portfolio deletion always asks what happens to the portfolio's projects: cascade-delete them, or
// keep them and just unset their portfolioId.
export const projectManagementDeletePortfolio = DeclareMethod<{ id: string, mode: 'deleteProjects' | 'keepProjects' }, { projects: number }>('projectManagementDeletePortfolio');

// A portfolio list row carries its (server-computed) project count for the catalog + delete dialog.
export interface ProjectManagementPortfolioRow extends ProjectManagementPortfolio {
  projectCount?: number;
}

type PortfolioRefs = References<ProjectManagementPortfolio>

export const projectManagementPortfolioModelClass: ModelClass<ProjectManagementPortfolio, PortfolioRefs> = {
  collection: ProjectManagementPortfolios,
  isOrgOwned: true,
  // orgId belongs here even though no form edits it: createMethodHandler stamps it from the acting
  // user BEFORE pluck(doc, editableFields), so leaving it out silently dropped it and produced rows no
  // publication or list query could see. Every other org-owned model lists it for the same reason.
  editableFields: ['active', 'orgId', 'name', 'logo', 'note'],
  listMethod: DeclareMethod<ListMethodArg<ProjectManagementPortfolio>, ListMethodResponse<ProjectManagementPortfolio, PortfolioRefs>>('projectManagementListPortfolios'),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectManagementPortfolio, PortfolioRefs>>('projectManagementGetPortfolio'),
  createMethod: DeclareMethod<Partial<ProjectManagementPortfolio>, string>('projectManagementCreatePortfolio'),
  updateMethod: DeclareMethod<Partial<ProjectManagementPortfolio>, string>('projectManagementUpdatePortfolio'),
  // The standard delete only handles EMPTY portfolios (form-page Deactivate); a portfolio with projects
  // must go through the list page's two-choice dialog (projectManagementDeletePortfolio).
  deleteMethod: DeclareMethod<string>('projectManagementDeleteEmptyPortfolio'),
  pathPrefix: '/project-management/portfolios',
  modelName: strings.Portfolio(),
  pluralName: strings.Portfolios(),
  asString: (document) => document.name || document._id,
  /**
   * Builds a Mongo query matching portfolios by id or name/note substring.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { note: new RegExp(search, 'gi') },
      ],
    };
  },
  /**
   * Validates a portfolio's fields.
   * @param portfolio - partial portfolio to validate
   * @returns map of field name to validation errors
   */
  validate: async (portfolio: Partial<ProjectManagementPortfolio>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, portfolio, 'active');
    validateString(errors, portfolio, 'name', { required: true });
    validateString(errors, portfolio, 'note');
    return errors;
  },
};
// --- Party model classes. Two collections, ONE builder: owners and epcs differ only in which
//     collection they read and what they are called, so the CRUD, search and validation are defined
//     once. Each gets the standard generic handlers server-side, which also gives RichSelect the
//     paged listMethod it needs for the picker. ---
type PartyRefs = References<ProjectManagementParty>

/**
 * Builds the ModelClass for a party registry.
 * @param key - method-name segment ('Owner' / 'Epc'); must be unique, it names the DDP methods
 * @param collection - the backing collection
 * @param labels - display names for the model and its plural
 * @returns the ModelClass, wired for the generic handlers and RichSelect
 */
const projectManagementPartyModelClass = (
  key: 'Owner' | 'Epc' | 'Utility',
  collection: Mongo.Collection<ProjectManagementParty>,
  labels: { modelName: string, pluralName: string, pathPrefix: string },
): ModelClass<ProjectManagementParty, PartyRefs> => ({
  collection,
  isOrgOwned: true,
  // orgId: see the portfolio model class above — it is stamped before the pluck, so it must survive it.
  editableFields: ['active', 'orgId', 'name', 'logo', 'website', 'note'],
  listMethod: DeclareMethod<ListMethodArg<ProjectManagementParty>, ListMethodResponse<ProjectManagementParty, PartyRefs>>(`projectManagementList${key}s`),
  getMethod: DeclareMethod<string, GetMethodResponse<ProjectManagementParty, PartyRefs>>(`projectManagementGet${key}`),
  createMethod: DeclareMethod<Partial<ProjectManagementParty>, string>(`projectManagementCreate${key}`),
  updateMethod: DeclareMethod<Partial<ProjectManagementParty>, string>(`projectManagementUpdate${key}`),
  deleteMethod: DeclareMethod<string>(`projectManagementDelete${key}`),
  modelName: labels.modelName,
  pluralName: labels.pluralName,
  // ModelFormPage/ModelListPage THROW without this — it is where they redirect after a save and what
  // the breadcrumb links back to.
  pathPrefix: labels.pathPrefix,
  asString: (document) => document.name || document._id,
  /**
   * Builds a Mongo query matching parties by id or name/note substring.
   * @param search - free-text search term
   * @returns Mongo selector ({} when search is empty)
   */
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return { $or: [{ _id: search }, { name: new RegExp(search, 'gi') }, { note: new RegExp(search, 'gi') }] };
  },
  /**
   * Validates a party's fields.
   * @param party - partial party to validate
   * @returns map of field name to validation errors
   */
  validate: async (party: Partial<ProjectManagementParty>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, party, 'active');
    validateString(errors, party, 'name', { required: true });
    validateString(errors, party, 'website');
    validateString(errors, party, 'note');
    return errors;
  },
});

export const projectManagementOwnerModelClass = projectManagementPartyModelClass('Owner', ProjectManagementOwners, { modelName: strings.Owner(), pluralName: strings.Owners(), pathPrefix: '/project-management/owners' });
export const projectManagementEpcModelClass = projectManagementPartyModelClass('Epc', ProjectManagementEpcs, { modelName: strings.Epc(), pluralName: strings.Epcs(), pathPrefix: '/project-management/epcs' });
export const projectManagementUtilityModelClass = projectManagementPartyModelClass('Utility', ProjectManagementUtilities, { modelName: strings.UtilityCompany(), pluralName: strings.Utilities(), pathPrefix: '/project-management/utilities' });

export const projectManagementSaveContact = DeclareMethod<Partial<ProjectManagementContact> & { projectManagementProjectId: string }, string>('projectManagementSaveContact');
export const projectManagementDeleteContact = DeclareMethod<{ id: string }>('projectManagementDeleteContact');
export const projectManagementSaveLink = DeclareMethod<Partial<ProjectManagementLink> & { projectManagementProjectId: string }, string>('projectManagementSaveLink');
export const projectManagementDeleteLink = DeclareMethod<{ id: string }>('projectManagementDeleteLink');
export const projectManagementGetNotificationPreference = DeclareMethod<void, ProjectManagementNotificationPreference | undefined>('projectManagementGetNotificationPreference');
export const projectManagementSaveNotificationPreference = DeclareMethod<{ off: { [type: string]: ProjectManagementNotificationChannel[] } }, void>('projectManagementSaveNotificationPreference');
export const projectManagementGetOrgSettings = DeclareMethod<void, ProjectManagementOrgSettings | undefined>('projectManagementGetOrgSettings');
export const projectManagementSaveVerticalSettings = DeclareMethod<{ lifecycleKey: string, values: Partial<Record<ProjectManagementVerticalSettingsField, string>> }, void>('projectManagementSaveVerticalSettings');
export const projectManagementSendVerticalDocumentDigest = DeclareMethod<{ lifecycleKey: string, day?: string }, ProjectManagementVerticalDigestResult>('projectManagementSendVerticalDocumentDigest');
export const projectManagementSaveChangeOrder = DeclareMethod<Partial<ProjectManagementChangeOrder> & { projectManagementProjectId: string }, string>('projectManagementSaveChangeOrder');
export const projectManagementDeleteChangeOrder = DeclareMethod<{ id: string }>('projectManagementDeleteChangeOrder');
export const projectManagementSetChangeOrderStatus = DeclareMethod<{ id: string, status: ProjectManagementChangeOrder['status'], decisionNote?: string }>('projectManagementSetChangeOrderStatus');
export const projectManagementSetProjectStatus = DeclareMethod<{ projectManagementProjectId: string, status: ProjectManagementProjectStatus, note?: string }>('projectManagementSetProjectStatus');
export const projectManagementSavePurchaseOrder = DeclareMethod<Partial<ProjectManagementPurchaseOrder> & { projectManagementProjectId: string }, string>('projectManagementSavePurchaseOrder');
export const projectManagementDeletePurchaseOrder = DeclareMethod<{ id: string }>('projectManagementDeletePurchaseOrder');
export const projectManagementBudgetAttachmentKinds = ['fundingSource', 'cashEvent', 'changeOrder', 'purchaseOrder'] as const;
export type ProjectManagementBudgetAttachmentKind = typeof projectManagementBudgetAttachmentKinds[number];

/**
 * The budget-record kinds whose attachments this caller may NOT see. It is the Budget tab's own rule, so an
 * attachment reaches exactly the people the tab would show it to: finance viewers see the attachments of
 * every record they can open there, and a funding source's only when they can see funding sources.
 * @param user - the caller
 * @param account - the caller's account
 * @returns the hidden kinds; empty when every attachment is visible
 */
export const projectManagementHiddenBudgetKinds = (
  user?: { isSuperUser?: boolean } | null,
  account?: { roles?: string[] } | null,
): ProjectManagementBudgetAttachmentKind[] => {
  if (!projectManagementCanSeeFinances(user, account)) return [...projectManagementBudgetAttachmentKinds];
  return projectManagementCanSeeFundingSources(user, account) ? [] : ['fundingSource'];
};

/**
 * Whether the caller may see this document. Anything that is not a Budget attachment answers true: the
 * other document rules (member doc types, org access) are applied where they always were.
 * @param hiddenKinds - projectManagementHiddenBudgetKinds for the caller
 * @param doc - a document, version or event, any of which may carry a budgetRef
 * @returns false only for a Budget attachment of a hidden kind
 */
export const projectManagementBudgetRefVisible = (
  hiddenKinds: readonly ProjectManagementBudgetAttachmentKind[],
  doc?: { budgetRef?: { kind: ProjectManagementBudgetAttachmentKind } } | null,
): boolean => !doc?.budgetRef || !hiddenKinds.includes(doc.budgetRef.kind);

/**
 * The selector fragment that withholds hidden Budget attachments from a documents, versions or events
 * query. A row with no budgetRef passes ($nin matches a missing field).
 * @param hiddenKinds - projectManagementHiddenBudgetKinds for the caller
 * @returns a Mongo selector fragment, empty when nothing is hidden
 */
export const projectManagementBudgetRefSelector = (hiddenKinds: readonly ProjectManagementBudgetAttachmentKind[]) =>
  (hiddenKinds.length ? { 'budgetRef.kind': { $nin: [...hiddenKinds] } } : {});
// Attach a supporting document to any budget record (funding source, cash source/use, CO, PO).
export const projectManagementAddBudgetAttachment = DeclareMethod<{ kind: ProjectManagementBudgetAttachmentKind, id: string, file: FileMeta }, string>('projectManagementAddBudgetAttachment');
// Project invoices — CRUD, member-allowed (project-scoped). The uploader (or a super user) can edit/delete.
export const projectManagementCreateInvoice = DeclareMethod<{ projectManagementProjectId: string, label: string, file: FileMeta, vendor?: string, invoiceNumber?: string, amount?: number, memo?: string }, string>('projectManagementCreateInvoice');
// Best-effort AI read of an uploaded invoice file (PDF/image) to pre-fill the upload form. Returns {} when it can't.
export const projectManagementParseInvoice = DeclareMethod<{ file: FileMeta }, { vendor?: string, invoiceNumber?: string, amount?: number }>('projectManagementParseInvoice');
// Super-user decision on an uploaded invoice: approve (stamp approvedBy/At) or reject (soft-delete, active:false).
// 'unapprove' takes an approval back without touching the invoice itself, the way a document version goes
// back to 'pending'. It is NOT 'reject', which soft-deletes the invoice.
export const projectManagementDecideInvoice = DeclareMethod<{ _id: string, decision: 'approve' | 'unapprove' | 'reject' }, void>('projectManagementDecideInvoice');
export const projectManagementUpdateInvoice = DeclareMethod<{ _id: string, label?: string, file?: FileMeta, vendor?: string, invoiceNumber?: string, luminiaInvoiceNumber?: string, amount?: number | null, memo?: string }, void>('projectManagementUpdateInvoice');
export const projectManagementDeleteInvoice = DeclareMethod<{ _id: string }, void>('projectManagementDeleteInvoice');
// Admin tier: allocate an invoice across money-out cash 'uses' (the QuickBooks Tier-0 match reads these).
export const projectManagementSetInvoiceAllocations = DeclareMethod<{ _id: string, allocations: { cashEventId: string, amount: number }[] }, void>('projectManagementSetInvoiceAllocations');
// Content search over the project's documents (current versions): title matches are client-side; this
// returns docs whose extracted text contains the query, with a context snippet.
export const projectManagementSearchDocuments = DeclareMethod<{ projectManagementProjectId: string, query: string }, { documentId: string, snippet: string }[]>('projectManagementSearchDocuments');
// Send-a-link consumption capture: find-or-create the PM project's UtilityAccount and return the public
// intake link (/uai) — the same flow the projects Dataroom uses; automations then pull the intervals.
export const projectManagementMakeUtilityAccountLink = DeclareMethod<{ projectManagementProjectId: string }, string>('projectManagementMakeUtilityAccountLink');
export const projectManagementGeocodeMissing = DeclareMethod<{ lifecycleKey?: string }, { geocoded: number }>('projectManagementGeocodeMissing');
export const projectManagementSetMilestoneOwner = DeclareMethod<{ milestoneId: string, stakeholderIds: string[] }>('projectManagementSetMilestoneOwner');
/** The reviewer's decision on a reviewed milestone. A 'deficiency' requires a note; the note is optional
 *  on the other two and is kept in History either way. */
export const projectManagementSetMilestoneReview = DeclareMethod<{ milestoneId: string, status: ProjectManagementMilestoneReviewStatus, note?: string }>('projectManagementSetMilestoneReview');
export const projectManagementSetTaskOwner = DeclareMethod<{ taskId: string, stakeholderIds: string[] }>('projectManagementSetTaskOwner');
// Free-form notes on a task. Same reach as completing it: admins anywhere, members on their own tasks.
export const projectManagementSetTaskNotes = DeclareMethod<{ taskId: string, notes: string }>('projectManagementSetTaskNotes');
// A task's note feed. Writing one has the same reach as the line above — admins anywhere, members on their
// own tasks — but editing or deleting one is the author's alone, or a super user's.
export const projectManagementSaveTaskNote = DeclareMethod<{ _id?: string, projectManagementTaskId?: string, body: string }, string>('projectManagementSaveTaskNote');
export const projectManagementDeleteTaskNote = DeclareMethod<{ id: string }>('projectManagementDeleteTaskNote');
/** Whether this user may edit or delete a note: its author, or a super user. */
export const projectManagementCanEditTaskNote = (user?: { _id?: string, isSuperUser?: boolean } | null, note?: { createdByUserId?: string } | null): boolean =>
  !!user && !!note && (!!user.isSuperUser || user._id === note.createdByUserId);
// --- Bulk grid (portfolio-wide spreadsheet editor). Method-fetched pages, NOT a subscription: the grid
//     is a deliberate snapshot so rows never reorder or repaint under someone mid-edit. Freshness comes
//     from Refresh + paging, and staleness is caught by the per-cell `updatedAt` tokens below. ---
export interface ProjectManagementBulkColumn {
  kind: 'spine' | 'parameter' | 'document';
  key: string;                                 // spine field name | parameter name | docType
  field?: 'estimatedValue' | 'actualValue';    // parameters only
}
export interface ProjectManagementBulkCell {
  value?: ProjectManagementParameterValue;
  id?: string;                                 // parameter / document _id (absent = not created yet)
  updatedAt?: Date;                            // concurrency token for THIS cell's underlying doc
  type?: ProjectManagementParameterType;
  unit?: string;
  status?: string;                             // documents
  taskId?: string;                             // parameters — drives the "blocking" filter
  fileUrl?: string;                            // documents — current version's file, when uploaded
}
export interface ProjectManagementBulkRow {
  projectId: string;
  luminiaId?: string;   // what the export prints in its "Project ID" column, when the project has one
  name: string;
  updatedAt?: Date;                            // token for this row's SPINE cells
  cells: { [colId: string]: ProjectManagementBulkCell };
}
/** Parses one pasted / spreadsheet cell into a stored value. Shared by the grid's clipboard paste and
 *  the xlsx import so a re-uploaded sheet is read exactly like a paste. Empty text = an explicit clear. */
export const projectManagementParseCellValue = (
  type: ProjectManagementParameterType | 'number' | 'string' | undefined, text: string,
): ProjectManagementParameterValue | null => {
  const t = `${text ?? ''}`.trim();
  if (!t) return null;
  if (type === 'boolean') return /^(y|yes|true|1)$/i.test(t) ? true : /^(n|no|false|0)$/i.test(t) ? false : null;
  if (type === 'date' || type === 'datetime') {
    // A bare YYYY-MM-DD is a CALENDAR date, not an instant: new Date('2026-07-14') is UTC midnight,
    // which renders as the 13th anywhere west of UTC. Parse it as local midnight, matching how the
    // parameter editor stores a typed date.
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t}T00:00:00` : t);
    return isNaN(d.getTime()) ? null : d;
  }
  if (type === 'string') return t;
  const n = Number(t.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** colId encoding shared by client and server. A CLIENT MAP KEY ONLY — parameter names are free text
 *  and could contain a colon, so the wire always carries the structured column, never this string. */
export const projectManagementBulkColId = (c: ProjectManagementBulkColumn): string => `${c.kind}:${c.key}:${c.field || ''}`;

/** The spine fields a project exposes for editing, sorting and filtering. Single source of truth —
 *  `projectManagementMethods` re-exports it for the write allowlist and the xlsx import catalogue. */
export const projectManagementEditableProjectFields = [
  'name', 'address', 'onsiteContactName', 'onsiteEmail', 'onsitePhone', 'clientId', 'portfolioId',
  'utilityId', 'sgipId', 'ownerId', 'epcId', 'logo', 'capacityKwDc', 'storageKwh', 'externalId', 'lat', 'lng',
] as const;

/** Editable but deliberately not offered as grid columns: coordinates are derived from the address,
 *  and the owner logo is a file, which the grid cannot render or coerce. */
const projectManagementUnpickableSpineFields: string[] = ['lat', 'lng', 'logo'];

/** The two status fields are grid columns WITHOUT being in the editable allowlist above: they have a write
 *  path of their own (projectManagementSetProjectStatus — the reason rule the exception statuses carry, the
 *  statusChangedAt/By stamps, the statusChanged history event), and putting them in the allowlist would
 *  let applyProjectUpdate bypass all of it. Bulk apply routes them there by name. */
export const projectManagementBulkStatusFields = ['status', 'statusNote'] as const;

/** Spine fields the column picker offers. Derived, so the catalogue can never drift from the allowlist —
 *  plus the status pair, the one deliberate exception (see above). */
export const projectManagementBulkSpineColumns: string[] = [
  ...projectManagementEditableProjectFields.filter((f) => !projectManagementUnpickableSpineFields.includes(f)),
  ...projectManagementBulkStatusFields,
];

/** Which widget/coercion a spine column uses (parameters carry their own type). */
export const projectManagementSpineFieldType = (key: string): 'number' | 'string' =>
  (['capacityKwDc', 'storageKwh', 'lat', 'lng'].includes(key) ? 'number' : 'string');

/** True when a stored cell counts as EMPTY. Blank is written as a missing field ($unset), but null and
 *  '' are covered too so the rule survives a writer that stores them. Mirror of `projectManagementBulkBlankClause`. */
export const projectManagementBulkIsBlank = (v?: ProjectManagementParameterValue | null): boolean =>
  v == null || v === '';

/** The Mongo form of the same rule — `{ f: null }` already matches a MISSING field as well as an explicit null. */
export const projectManagementBulkBlankClause = (field: string): any => ({ $or: [{ [field]: null }, { [field]: '' }] });

export interface ProjectManagementBulkFilter {
  lifecycleKey?: string;
  search?: string;
  portfolioId?: string;
  onlyMissingActuals?: boolean;   // only projects/rows with a task-linked parameter lacking an actual
  columnFilters?: ProjectManagementBulkColumnFilter[];
}

// --- Excel-style per-column filters. See miscl/PMspecs3/bulk_column_filters_plan.md ---------------
export type ProjectManagementBulkFilterOp =
  | 'contains' | 'notContains' | 'is' | 'isNot' | 'startsWith' | 'endsWith'
  | 'gt' | 'gte' | 'lt' | 'lte' | 'between'
  | 'before' | 'onOrBefore' | 'after' | 'onOrAfter'
  | 'isTrue' | 'isFalse' | 'isBlank' | 'isNotBlank'
  | 'hasFile' | 'noFile'
  | 'values';                                  // the checkbox list; operand is values[] + blank

export interface ProjectManagementBulkColumnFilter {
  'column': ProjectManagementBulkColumn;       // structured, never a colId string
  'op': ProjectManagementBulkFilterOp;
  'values'?: string[];                         // keys from projectManagementBulkValueKey
  'blank'?: boolean;                           // '(Blanks)' ticked — carried apart from values[]
  'text'?: string;
  'num'?: number;
  'num2'?: number;                             // numbers: `between` is INCLUSIVE of both ends
  // Dates arrive as a resolved half-open interval [date, date2) of ABSOLUTE instants, computed on the
  // CLIENT in the user's timezone (plan §7.5). Either end may be absent for an open-ended comparison.
  // The server never parses a date or does calendar math — prod runs UTC and would shift every
  // boundary 7-8 hours. 'before 8/4' sends only date2; 'after 8/4' sends only date.
  'date'?: Date;
  'date2'?: Date;
}
export interface ProjectManagementBulkSort {
  'column': ProjectManagementBulkColumn;
  'dir': 1 | -1;
}

/** Caps on client-supplied filter state — it arrives from user-editable localStorage. */
export const projectManagementBulkColumnFiltersMax = 20;
export const projectManagementBulkFilterValuesMax = 700;   // must exceed 566 projects — plan §1.2
export const projectManagementBulkFilterTextMax = 200;

/**
 * Stable identity for one distinct cell value, TYPE-TAGGED so 100 and '100' never collide.
 * Keys on the STORED value: projectManagementFormatValue is lossy (2dp, whole dollars), so two
 * distinct values can share a display label and must still be selectable apart.
 */
export const projectManagementBulkValueKey = (v?: ProjectManagementParameterValue | null): string => {
  if (projectManagementBulkIsBlank(v)) return 'blank';
  if (v instanceof Date) return `d:${v.getTime()}`;
  if (typeof v === 'boolean') return `b:${v}`;
  if (typeof v === 'number') return `n:${v}`;
  return `s:${v}`;
};

/** Inverse of projectManagementBulkValueKey — spine filters compile to Mongo and need the raw value back. */
export const projectManagementBulkValueFromKey = (key: string): ProjectManagementParameterValue | null => {
  if (key === 'blank') return null;
  const rest = key.slice(2);
  switch (key.slice(0, 2)) {
    case 'd:': return new Date(Number(rest));
    case 'b:': return rest === 'true';
    case 'n:': return Number(rest);
    default: return rest;
  }
};

/**
 * True when a filter carries no usable operand and must be skipped ENTIRELY rather than compiled.
 * An empty `contains` would otherwise match nothing and an empty `notContains` everything, and a
 * `values` arm with no arms compiles to `{ $or: [] }`, which Mongo rejects.
 */
export const projectManagementBulkFilterIsInert = (f: ProjectManagementBulkColumnFilter): boolean => {
  const text = !!f.text?.trim();
  const num = typeof f.num === 'number' && Number.isFinite(f.num);
  const num2 = typeof f.num2 === 'number' && Number.isFinite(f.num2);
  const date = f.date instanceof Date && !isNaN(f.date.getTime());
  const date2 = f.date2 instanceof Date && !isNaN(f.date2.getTime());
  switch (f.op) {
    case 'values': return !f.values?.length && !f.blank;
    case 'contains': case 'notContains': case 'startsWith': case 'endsWith': return !text;
    // is/isNot are shared by text, number and date columns, each carrying its own operand.
    case 'is': case 'isNot': return !text && !num && !date && !date2;
    case 'gt': case 'gte': case 'lt': case 'lte': return !num;
    // Date bounds are half-open [date, date2); an open-ended comparison fills only one end.
    case 'before': case 'onOrBefore': case 'after': case 'onOrAfter': return !date && !date2;
    case 'between': return !(num && num2) && !(date && date2);
    default: return false;                     // isBlank/isNotBlank/isTrue/isFalse/hasFile/noFile need no operand
  }
};

export const projectManagementBulkGrid = DeclareMethod<
  ProjectManagementBulkFilter & {
    columns: ProjectManagementBulkColumn[], sort?: ProjectManagementBulkSort, skip: number, limit: number,
    // Projects to keep in the result even if they no longer match — the rows the user just edited.
    pinnedIds?: string[],
  },
  { total: number, universe: number, rows: ProjectManagementBulkRow[] }
>('projectManagementBulkGrid');

// The value list behind a column's checkbox panel. Returns RAW values, never labels: formatting runs
// through toLocaleDateString(), which on the server would use the SERVER's timezone (prod is UTC) and
// produce a label that doesn't match the cell it selects. The popover formats and groups.
export interface ProjectManagementBulkColumnValue {
  key: string;
  value?: ProjectManagementParameterValue;
  count: number;
}
export const projectManagementBulkColumnValues = DeclareMethod<
  ProjectManagementBulkFilter & { columns: ProjectManagementBulkColumn[], column: ProjectManagementBulkColumn, limit?: number },
  { values: ProjectManagementBulkColumnValue[], blankCount: number, truncated: boolean }
>('projectManagementBulkColumnValues');

// --- Saved column presets. Org-shared on purpose: the built-in presets are team conventions, and a
//     hand-built column set ("QA columns") is worth the same to everyone. Stored rather than derived,
//     so unlike the built-ins these do NOT follow template changes — see projectManagementBulkPresetPrune. ---
export interface ProjectManagementBulkPreset extends Doc {
  orgId?: string;
  lifecycleKey: string;                          // presets are per-lifecycle; columns mean nothing across templates
  name: string;
  columns: ProjectManagementBulkColumn[];
}
export const ProjectManagementBulkPresets = new Mongo.Collection<ProjectManagementBulkPreset>('projectManagementBulkPresets');

export const projectManagementSaveBulkPreset = DeclareMethod<
  { _id?: string, name: string, lifecycleKey: string, columns: ProjectManagementBulkColumn[] }, string
>('projectManagementSaveBulkPreset');
export const projectManagementDeleteBulkPreset = DeclareMethod<{ _id: string }>('projectManagementDeleteBulkPreset');

/** Hard cap per org, so a runaway client can't fill the dropdown (or the collection). */
export const projectManagementBulkPresetsMax = 50;

/**
 * Drops columns a saved preset refers to that the lifecycle no longer offers. Saved presets are a
 * frozen snapshot, so a renamed or retired parameter would otherwise render as a dead column that
 * silently returns nothing — the same staleness the column picker guards against on load.
 * @param columns - the preset's stored columns
 * @param valid - colIds the current catalogue offers
 */
export const projectManagementBulkPresetPrune = (
  columns: ProjectManagementBulkColumn[], valid: Set<string>,
): ProjectManagementBulkColumn[] => (columns || []).filter((c) => valid.has(projectManagementBulkColId(c)));

// Long ("Parameters") mode: a row per project × parameter, for filling one field across the portfolio.
export interface ProjectManagementBulkParameterRow {
  parameterId: string;
  projectId: string;
  projectName: string;
  name: string;
  type?: ProjectManagementParameterType;
  unit?: string;
  estimatedValue?: ProjectManagementParameterValue;
  actualValue?: ProjectManagementParameterValue;
  taskLabel?: string;
  updatedAt?: Date;
}
export const projectManagementBulkParameters = DeclareMethod<
  ProjectManagementBulkFilter & { names?: string[], skip: number, limit: number },
  { total: number, rows: ProjectManagementBulkParameterRow[], names: string[] }
>('projectManagementBulkParameters');

// One write path for every grid edit. `value: null` clears the cell; `expectedUpdatedAt` is the token of
// the doc actually being written (the parameter doc, or the project for spine fields).
export interface ProjectManagementBulkEdit {
  projectId: string;
  kind: 'spine' | 'parameter';
  key: string;                                 // spine field | parameter name
  field?: 'estimatedValue' | 'actualValue';
  value?: ProjectManagementParameterValue | null;
  expectedUpdatedAt?: Date;
}
export interface ProjectManagementBulkApplyResult {
  applied: number;
  conflicts: { projectId: string, key: string }[];
  errors: { projectId: string, key: string, message: string }[];
}
export const projectManagementBulkApply = DeclareMethod<
  { edits: ProjectManagementBulkEdit[], dryRun?: boolean },
  ProjectManagementBulkApplyResult
>('projectManagementBulkApply');
/** Server cap per call; the client chunks larger pastes into sequential calls. */
export const projectManagementBulkApplyMax = 500;

// xlsx round-trip: export the current grid, edit it in Excel, re-upload. The import maps columns back
// by their header LABEL (the same text the grid shows) and rows by a Project ID column, then feeds the
// very same bulkApply path — dryRun first so the user sees the diff before anything is written.
export interface ProjectManagementBulkImportResult {
  rows: number;
  cells: number;                                   // cells that differ from what's stored
  projects: number;
  unknownColumns: string[];
  errors: { projectId: string, key: string, message: string }[];
  applied?: number;
  // Rows with no Project ID: the sheet is asking for a new project. Named in the dry run so nobody
  // creates one by leaving a stray row behind, and counted separately because one of these is 150-odd
  // documents, not a cell.
  creates?: { row: number, name: string, lifecycleKey: string, lifecycleLabel: string }[];
  created?: number;
  // Rows that WOULD create but the caller may not: reported rather than silently dropped.
  createBlocked?: number;
}
export const projectManagementBulkExport = DeclareMethod<
  ProjectManagementBulkFilter & { columns: ProjectManagementBulkColumn[], sort?: ProjectManagementBulkSort },
  string
>('projectManagementBulkExport');
export const projectManagementBulkImport = DeclareMethod<
  // lifecycleKey is the vertical the page is on: what a created row falls back to when its Vertical cell
  // is blank, which is the usual case for a row typed straight into an export.
  // createRows are the sheet row numbers the reviewer left ticked in the preview; absent means all of
  // them, which is what the dry run sends.
  { file: FileMeta, dryRun?: boolean, lifecycleKey?: string, createRows?: number[] },
  ProjectManagementBulkImportResult
>('projectManagementBulkImport');

// --- Document → LLM → suggested parameter estimates -------------------------------------------------
export interface ProjectManagementLlmParsePreview {
  documentLabel?: string;
  taskLabels: string[];
  parameters: { parameterId: string, name: string, type?: ProjectManagementParameterType, unit?: string }[];
  suggestions: { parameterId: string, name: string, value: ProjectManagementParameterValue }[];
  textChars: number;
  truncated: boolean;
  provider: string;
  model?: string;
  prompt?: string;
}
/** Dry run: assembles the prompt, calls the model and returns what it WOULD suggest. Writes nothing. */
export const projectManagementLlmParsePreview = DeclareMethod<{ projectManagementDocumentId: string, includePrompt?: boolean }, ProjectManagementLlmParsePreview>('projectManagementLlmParsePreview');
/** Re-run the parse for a document, creating fresh proposed suggestions (admin-triggered). */
export const projectManagementLlmParseDocument = DeclareMethod<{ projectManagementDocumentId: string }, { suggested: number }>('projectManagementLlmParseDocument');
/** Accept (write the estimate) or reject one or more proposed suggestions. */
export const projectManagementDecideSuggestions = DeclareMethod<{ suggestionIds: string[], accept: boolean }, { accepted: number, rejected: number, skipped: number }>('projectManagementDecideSuggestions');

/**
 * The pictures inside a document that is an archive, for the gallery viewer.
 *
 * Its own method rather than the drive's `driveGallery`, and deliberately so: /Projects in the drive is
 * ACL'd to need the Documents role on top of this one, and a project member looking at photographs on
 * their own task must not be asked for a second module's grant. Same index underneath, authorized here.
 */
export const projectManagementDocumentGallery = DeclareMethod<
  { versionId: string, from?: number, limit?: number },
  DriveGalleryPage
>('projectManagementDocumentGallery');

export const projectManagementRicImport = DeclareMethod<{ file: FileMeta }, ProjectManagementRicImportResult>('projectManagementRicImport');
// Wipes the org's RIC projects + all their child records so a re-import rebuilds from scratch (dev tool).
// lifecycleKey defaults to 'ric', so the RIC dev tool's call still names no vertical. `password` is the
// CALLER'S OWN, re-checked by the method: this deletes a portfolio and cannot be undone, so a logged-in
// session is not authority enough on its own.
export const projectManagementWipeRicProjects = DeclareMethod<{ lifecycleKey?: string, password: string }, ProjectManagementWipeRicResult>('projectManagementWipeRicProjects');
// Result of importing the Prepaid PPA Fund Model workbook onto the residential projects it prices.
export interface ProjectManagementFundModelImportResult {
  version?: string;      // the workbook's own "Prepaid PPA Fund Model vNNN"
  rows: number;          // projects in the workbook
  matched: number;       // of those, projects that exist here (by ClickUp id)
  unmatched: string[];   // ClickUp ids the workbook prices and this org does not have
  parameters: number;    // parameter values written (the contract fields and the fund's economics)
  sources: number;       // funding sources written
  uses: number;          // outbound cash events written
  unbalanced: string[];  // projects whose sources do not equal their uses — the model disagreeing with itself
  failedChecks: { label: string; value: string; count: number }[];  // the engine's own 'Chk:' columns, where they do not pass
  warnings: string[];
  errors: string[];
}

/** Imports the Prepaid PPA Fund Model workbook: every project it prices, found here by its ClickUp id. */
export const projectManagementResTpoFundModelImport = DeclareMethod<{ file: FileMeta }, ProjectManagementFundModelImportResult>('projectManagementResTpoFundModelImport');
/** Seeds Residential (TPO, non-SGIP) projects from the Residential Pipeline Tracker workbook. `dealer` is
 *  the partner whose pipeline the file is (one tracker is one dealer's), and lands as each project's EPC. */
export const projectManagementResTpoImport = DeclareMethod<{ file: FileMeta, dealer?: string }, ProjectManagementResTpoImportResult>('projectManagementResTpoImport');
/** Re-prices every residential project's dealer payments through the ordinary sync. One-off, and safe to
 *  repeat: a payment already carrying the right figure is skipped. `dryRun` reports what would change. */
export const projectManagementResyncResTpoPayments = DeclareMethod<{ dryRun?: boolean }, ProjectManagementResyncPaymentsResult>('projectManagementResyncResTpoPayments');
export interface ProjectManagementResyncPaymentsResult {
  projects: number;    // residential projects looked at
  repriced: number;    // of those, the ones whose payments changed
  payments: number;    // individual payments given a new figure
}
export const projectManagementDataRoomExport = DeclareMethod<{ projectManagementProjectIds: string[], docTypes: string[], approvedOnly?: boolean }, ProjectManagementDataRoomResult>('projectManagementDataRoomExport');
export const projectManagementBatchUploadDocument = DeclareMethod<{ projectManagementProjectId: string, docType: string, file: FileMeta }>('projectManagementBatchUploadDocument');

Meteor.isServer && Meteor.startup(async () => {
  await ProjectManagementProjects.createIndexAsync({ orgId: 1 });
  await ProjectManagementProjects.createIndexAsync({ lifecycleKey: 1 });
  await ProjectManagementProjects.createIndexAsync({ externalId: 1 });
  // Sparse: projects predating the backfill carry no luminiaId. Unique so a duplicate fails loudly.
  await ProjectManagementProjects.createIndexAsync({ luminiaId: 1 }, { unique: true, sparse: true });
  await ProjectManagementProjectInvoices.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementOrgSettings.createIndexAsync({ orgId: 1 }, { unique: true });
  await ProjectManagementProjectEvents.createIndexAsync({ projectManagementProjectId: 1, createdAt: -1 });
  await ProjectManagementTasks.createIndexAsync({ projectManagementProjectId: 1 });
  // Multikey, for the member publication: every task/milestone read a member makes filters on this array.
  await ProjectManagementTasks.createIndexAsync({ projectManagementProjectId: 1, ownerStakeholderIds: 1 });
  await ProjectManagementMilestones.createIndexAsync({ projectManagementProjectId: 1, ownerStakeholderIds: 1 });
  await ProjectManagementDocuments.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementDocumentVersions.createIndexAsync({ projectManagementDocumentId: 1, versionNumber: -1 });
  await ProjectManagementMilestones.createIndexAsync({ projectManagementProjectId: 1, order: 1 });
  await ProjectManagementCashSuggestions.createIndexAsync({ projectManagementProjectId: 1, status: 1 });
  await ProjectManagementCashSuggestions.createIndexAsync({ qbBillId: 1, qbLineNum: 1 });
  await ProjectManagementFundingSources.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementCashEvents.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementParameters.createIndexAsync({ projectManagementProjectId: 1 });
  // projectId-FIRST so it also prefixes the single-field indexes above and serves the grid's existing
  // hydration reads; the column-filter engine always scopes its batched read to a candidate id set.
  await ProjectManagementParameters.createIndexAsync({ projectManagementProjectId: 1, name: 1 });
  await ProjectManagementDocuments.createIndexAsync({ projectManagementProjectId: 1, docType: 1 });
  await ProjectManagementProjectEvents.createIndexAsync({ projectManagementProjectId: 1, kind: 1, 'ref.id': 1, createdAt: -1 });  // per-entity detail views (e.g. a parameter's history)
  await ProjectManagementStakeholders.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementChangeOrders.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementPurchaseOrders.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementContacts.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementLinks.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementTaskNotes.createIndexAsync({ projectManagementTaskId: 1, createdAt: -1 });  // the feed's own order
  await ProjectManagementTaskNotes.createIndexAsync({ projectManagementProjectId: 1 });
  await ProjectManagementNotificationPreferences.createIndexAsync({ accountId: 1 });
  await ProjectManagementBulkPresets.createIndexAsync({ orgId: 1, lifecycleKey: 1 });
  await ProjectManagementPortfolios.createIndexAsync({ orgId: 1 });
  await ProjectManagementProjects.createIndexAsync({ portfolioId: 1 });
  await ProjectManagementOwners.createIndexAsync({ orgId: 1, name: 1 });
  await ProjectManagementEpcs.createIndexAsync({ orgId: 1, name: 1 });
  await ProjectManagementUtilities.createIndexAsync({ orgId: 1, name: 1 });
});
